// path: src/pages/KursPanel.js
import React, { useEffect, useMemo, useState } from "react";
import ResimUpload from "../ResimUpload";

const API_ROOT = String(
  process.env.REACT_APP_API_BASE ||
    "https://panel.haymestasarim.com/api"
).replace(/\/+$/, "");

const API_BASE = /\/course$/i.test(API_ROOT)
  ? API_ROOT
  : `${API_ROOT}/course`;

const EMPTY_COURSE = {
  title_tr: "",
  title_en: "",
  title_de: "",
  category_id: "",
  short_description_tr: "",
  description_tr: "",
  curriculum_text: "",
  requirements_text: "",
  outcomes_text: "",
  tags_text: "",
  cover_image: "",
  gallery_text: "",
  promo_video_url: "",
  program_type: "education",
  sport_branch: "",
  target_audience: "",
  season: "",
  membership_model: "",
  guardian_required: false,
  health_form_required: false,
  attendance_enabled: true,
  assessment_enabled: false,
  equipment_notes: "",
  custom_fields_text: "",
  training_type: "face_to_face",
  level: "Başlangıç",
  language: "tr",
  age_min: "",
  age_max: "",
  duration_text: "",
  total_hours: "",
  certificate_enabled: true,
  certificate_title: "",
  online_platform: "",
  address: "",
  city: "",
  district: "",
  contact_phone: "",
  contact_email: "",
  whatsapp: "",
  base_price: "",
  sale_price: "",
  currency: "TRY",
  installment_count: 1,
  free: false,
  featured: false,
  applications_open: true,
  status: "draft",
};

const EMPTY_PERIOD = {
  course_id: "",
  title: "",
  start_date: "",
  end_date: "",
  application_deadline: "",
  days_text: "Pazartesi, Çarşamba, Cuma",
  start_time: "09:00",
  end_time: "12:00",
  capacity: 20,
  waitlist_capacity: 5,
  price: "",
  currency: "TRY",
  installment_count: 1,
  classroom: "",
  meeting_url: "",
  group_code: "",
  age_group: "",
  team_name: "",
  sport_level: "",
  billing_cycle: "one_time",
  recurring_months: 1,
  attendance_required: true,
  auto_session_title: "",
  status: "open",
  notes: "",
};

const EMPTY_INSTRUCTOR = {
  full_name: "",
  title: "",
  biography: "",
  photo_url: "",
  phone: "",
  email: "",
  specialties_text: "",
  active: true,
};

const EMPTY_SESSION = {
  course_id: "",
  period_id: "",
  title: "",
  description: "",
  session_date: "",
  start_time: "09:00",
  end_time: "12:00",
  classroom: "",
  meeting_url: "",
  instructor_id: "",
  session_type: "lesson",
  location_text: "",
  attendance_required: true,
  cancelled_reason: "",
  status: "planned",
};

const EMPTY_MANUAL_ENROLLMENT = {
  course_id: "",
  period_id: "",
  full_name: "",
  phone: "",
  email: "",
  birth_date: "",
  city: "",
  district: "",
  participant_type: "adult",
  guardian_name: "",
  guardian_phone: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  medical_notes: "",
  license_no: "",
  jersey_size: "",
  agreed_price: "",
  discount_amount: "",
  scholarship_amount: "",
  installment_count: 1,
  payment_preference: "Kurum kaydı",
  notes: "",
};

const STATUS_LABELS = {



  draft: "Taslak",
  published: "Yayında",
  paused: "Duraklatıldı",
  completed: "Tamamlandı",
  archived: "Arşiv",
  planned: "Planlandı",
  open: "Başvuru Açık",
  full: "Kontenjan Dolu",
  started: "Başladı",
  cancelled: "İptal",
  new: "Yeni",
  reviewing: "İnceleniyor",
  document_pending: "Belge Bekleniyor",
  pre_approved: "Ön Onay",
  payment_pending: "Ödeme Bekleniyor",
  approved: "Onaylandı",
  rejected: "Reddedildi",
  active: "Aktif",
  unpaid: "Ödenmedi",
  partial: "Kısmi Ödendi",
  paid: "Ödendi",
  present: "Katıldı",
  absent: "Katılmadı",
  late: "Geç Geldi",
  excused: "Mazeretli",
  medical: "Raporlu",
  trial: "Deneme Katılımı",
  online: "Online Katıldı",
  pending: "Onay Bekliyor",
  refunded: "İade Edildi",
  unmarked: "İşaretlenmedi",
};

const NAV_ITEMS = [
  ["overview", "Genel Bakış", "📊"],
  ["courses", "Kurslar & Programlar", "📚"],
  ["periods", "Dönemler & Gruplar", "🗓️"],
  ["applications", "Başvurular", "📥"],
  ["students", "Öğrenci & Sporcular", "👥"],
  ["finance", "Tahsilat", "💳"],
  ["sessions", "Program & Yoklama", "✅"],
  ["instructors", "Eğitmen & Antrenör", "🏅"],
  ["reports", "Raporlar", "📈"],
];

function tokenHeaders(json = false) {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token") || "";
  return {
    Accept: "application/json",
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function api(path, options = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...tokenHeaders(Boolean(options.body)), ...(options.headers || {}) },
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json.ok === false) {
    throw new Error(json.message || `HTTP ${response.status}`);
  }
  return json;
}

function money(value, currency = "TRY") {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: currency || "TRY",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function dateText(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "short", year: "numeric" }).format(date);
}

function dateTimeText(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function listFromText(value) {
  return String(value || "")
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function textFromList(value) {
  if (!Array.isArray(value)) return "";
  return value
    .map((item) => (typeof item === "string" ? item : item?.title || item?.name || ""))
    .filter(Boolean)
    .join("\n");
}

function statusTone(status) {
  if (["published", "approved", "active", "paid", "present", "completed", "open"].includes(status)) return "success";
  if (["rejected", "cancelled", "archived", "absent"].includes(status)) return "danger";
  if (["new", "reviewing", "partial", "payment_pending", "document_pending", "late"].includes(status)) return "warning";
  return "neutral";
}

export default function KursPanel() {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mobileNav, setMobileNav] = useState(false);

  const [dashboard, setDashboard] = useState({ stats: {}, latest_applications: [], upcoming_sessions: [], due_installments: [] });
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [applications, setApplications] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [report, setReport] = useState({ summary: {}, by_course: [], payments: [], applications: [] });
  const [fullReport, setFullReport] = useState({});
  const [paymentNotices, setPaymentNotices] = useState([]);
  const [todaySessions, setTodaySessions] = useState([]);

  const [courseForm, setCourseForm] = useState(EMPTY_COURSE);
  const [courseEditingId, setCourseEditingId] = useState(null);
  const [courseFormOpen, setCourseFormOpen] = useState(false);
  const [periodForm, setPeriodForm] = useState(EMPTY_PERIOD);
  const [periodEditingId, setPeriodEditingId] = useState(null);
  const [periodFormOpen, setPeriodFormOpen] = useState(false);
  const [instructorForm, setInstructorForm] = useState(EMPTY_INSTRUCTOR);
  const [instructorEditingId, setInstructorEditingId] = useState(null);
  const [instructorFormOpen, setInstructorFormOpen] = useState(false);

  const [sessionForm, setSessionForm] = useState(EMPTY_SESSION);
  const [sessionEditingId, setSessionEditingId] = useState(null);
  const [sessionFormOpen, setSessionFormOpen] = useState(false);

const [
  manualEnrollmentOpen,
  setManualEnrollmentOpen,
] = useState(false);

const [
  manualEnrollmentForm,
  setManualEnrollmentForm,
] = useState(EMPTY_MANUAL_ENROLLMENT);

const [selectedApplication, setSelectedApplication] =
  useState(null);


  const [applicationActionOpen, setApplicationActionOpen] = useState(false);
  const [applicationAction, setApplicationAction] = useState({ status: "reviewing", admin_note: "", rejection_reason: "", user_message: "", agreed_price: "", discount_amount: "", scholarship_amount: "", installment_count: 1 });

  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [studentDetailOpen, setStudentDetailOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: "", payment_method: "cash", payment_date: new Date().toISOString().slice(0, 10), reference_no: "", note: "" });
  const [certificateOpen, setCertificateOpen] = useState(false);
  const [certificateForm, setCertificateForm] = useState({ title: "", issue_date: new Date().toISOString().slice(0, 10), file_url: "" });

  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [attendanceSession, setAttendanceSession] = useState(null);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [assessmentForm, setAssessmentForm] = useState({ title: "", assessment_type: "progress", score: "", max_score: "100", grade: "", note: "", visible_to_student: true });

  const [reportDates, setReportDates] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: start.toISOString().slice(0, 10), end: now.toISOString().slice(0, 10) };
  });

  async function loadAll(silent = false) {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [dashboardJson, categoryJson, courseJson, periodJson, applicationJson, enrollmentJson, sessionJson, instructorJson, reportJson, fullReportJson, noticeJson, todayJson] = await Promise.all([
        api("/dashboard"),
        api("/categories"),
        api("/courses"),
        api("/periods"),
        api("/applications"),
        api("/enrollments"),
        api("/sessions"),
        api("/instructors"),
        api(`/reports/summary?start=${reportDates.start}&end=${reportDates.end}`),
        api(`/reports/full?start=${reportDates.start}&end=${reportDates.end}`),
        api("/payment-notices?status=all"),
        api("/attendance/today"),
      ]);
      setDashboard(dashboardJson.data || {});
      setCategories(categoryJson.data || []);
      setCourses(courseJson.data || []);
      setPeriods(periodJson.data || []);
      setApplications(applicationJson.data || []);
      setEnrollments(enrollmentJson.data || []);
      setSessions(sessionJson.data || []);
      setInstructors(instructorJson.data || []);
      setReport(reportJson.data || {});
      setFullReport(fullReportJson.data || {});
      setPaymentNotices(noticeJson.data || []);
      setTodaySessions(todayJson.data || []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Kurs verileri alınamadı.");
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(""), 3500);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const stats = dashboard.stats || {};

  const filteredCourses = useMemo(() => {
    const query = search.toLocaleLowerCase("tr-TR");
    return courses.filter((item) => {
      const matchesSearch = !query || [item.title_tr, item.category_name, item.city, item.district].join(" ").toLocaleLowerCase("tr-TR").includes(query);
      const matchesStatus = statusFilter === "all" || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [courses, search, statusFilter]);

  const filteredPeriods = useMemo(() => {
    const query = search.toLocaleLowerCase("tr-TR");
    return periods.filter((item) => {
      const matchesSearch = !query || [item.title, item.course_title, item.classroom].join(" ").toLocaleLowerCase("tr-TR").includes(query);
      const matchesStatus = statusFilter === "all" || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [periods, search, statusFilter]);

  const filteredApplications = useMemo(() => {
    const query = search.toLocaleLowerCase("tr-TR");
    return applications.filter((item) => {
      const matchesSearch = !query || [item.full_name, item.phone, item.email, item.course_title, item.application_no].join(" ").toLocaleLowerCase("tr-TR").includes(query);
      const matchesStatus = statusFilter === "all" || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [applications, search, statusFilter]);

  const filteredEnrollments = useMemo(() => {
    const query = search.toLocaleLowerCase("tr-TR");
    return enrollments.filter((item) => {
      const matchesSearch = !query || [item.full_name, item.phone, item.course_title, item.enrollment_no].join(" ").toLocaleLowerCase("tr-TR").includes(query);
      const matchesStatus = statusFilter === "all" || item.status === statusFilter || item.payment_status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [enrollments, search, statusFilter]);

 const receivableRows = useMemo(
  () =>
    filteredEnrollments.filter(
      (item) =>
        Number(item.remaining_amount || 0) > 0
    ),
  [filteredEnrollments]
);

const pendingPaymentNotices = useMemo(
  () =>
    paymentNotices.filter(
      (item) => item.status === "pending"
    ),
  [paymentNotices]
);

const pendingPaymentEnrollmentIds = useMemo(
  () =>
    new Set(
      pendingPaymentNotices.map(
        (item) => String(item.enrollment_id)
      )
    ),
  [pendingPaymentNotices]
);


  function resetFilters() {
    setSearch("");
    setStatusFilter("all");
  }

  function openNewCourse() {
    setCourseEditingId(null);
    setCourseForm(EMPTY_COURSE);
    setCourseFormOpen(true);
  }

  function editCourse(course) {
    setCourseEditingId(course.id);

    setCourseForm({
      ...EMPTY_COURSE,
      ...course,
      category_id: course.category_id || "",
      curriculum_text: textFromList(course.curriculum),
      requirements_text: textFromList(course.requirements),
      outcomes_text: textFromList(course.outcomes),
      tags_text: textFromList(course.tags),
      gallery_text: textFromList(course.gallery),
    });

    setCourseFormOpen(true);
  }

  function addGalleryImage(url) {
    const cleanUrl = String(url || "").trim();

    if (!cleanUrl) return;

    setCourseForm((previous) => {
      const currentImages = listFromText(
        previous.gallery_text,
      );

      if (currentImages.includes(cleanUrl)) {
        return previous;
      }

      return {
        ...previous,
        gallery_text: [
          ...currentImages,
          cleanUrl,
        ].join("\n"),
      };
    });
  }

  function removeGalleryImage(url) {
    setCourseForm((previous) => ({
      ...previous,

      gallery_text: listFromText(
        previous.gallery_text,
      )
        .filter((item) => item !== url)
        .join("\n"),
    }));
  }

  async function saveCourse(event) {


    event.preventDefault();
    if (!courseForm.title_tr.trim()) return setNotice("Kurs / program adı zorunludur.");
    setBusy(true);
    try {
      const payload = {
        ...courseForm,
        category_id: courseForm.category_id || null,
        curriculum: listFromText(courseForm.curriculum_text),
        requirements: listFromText(courseForm.requirements_text),
        outcomes: listFromText(courseForm.outcomes_text),
        tags: listFromText(courseForm.tags_text),
        gallery: listFromText(courseForm.gallery_text),
        custom_fields: listFromText(courseForm.custom_fields_text).map((label) => ({ label, type: "text", required: false })),
      };
      await api(courseEditingId ? `/courses/${courseEditingId}` : "/courses", {
        method: courseEditingId ? "PUT" : "POST",
        body: JSON.stringify(payload),
      });
      setNotice(courseEditingId ? "Kurs / program güncellendi." : "Kurs / program oluşturuldu.");
      setCourseFormOpen(false);
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function archiveCourse(course) {
    if (!window.confirm(`${course.title_tr} arşive alınsın mı?`)) return;
    setBusy(true);
    try {
      await api(`/courses/${course.id}`, { method: "DELETE" });
      setNotice("Kurs arşive alındı.");
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteCoursePermanently(course) {
    const answer = window.prompt(
      `${course.title_tr} kalıcı olarak silinecek.\n\n` +
        "Başvuru veya kursiyer bağlantısı varsa sistem silmeye izin vermez.\n" +
        "Devam etmek için SİL yazın.",
    );

    if (
      String(answer || "")
        .trim()
        .toLocaleUpperCase("tr-TR") !== "SİL"
    ) {
      return;
    }

    setBusy(true);

    try {
      await api(`/courses/${course.id}/permanent`, {
        method: "DELETE",
      });

      setNotice("Kurs / program kalıcı olarak silindi.");
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  function openNewPeriod(courseId = "") {
    const course = courses.find((item) => String(item.id) === String(courseId));
    setPeriodEditingId(null);
    setPeriodForm({
      ...EMPTY_PERIOD,
      course_id: courseId || "",
      price: course?.sale_price || course?.base_price || "",
      currency: course?.currency || "TRY",
      installment_count: course?.installment_count || 1,
    });
    setPeriodFormOpen(true);
  }

  function editPeriod(period) {
    setPeriodEditingId(period.id);
    setPeriodForm({
      ...EMPTY_PERIOD,
      ...period,
      course_id: period.course_id,
      start_date: period.start_date ? String(period.start_date).slice(0, 10) : "",
      end_date: period.end_date ? String(period.end_date).slice(0, 10) : "",
      application_deadline: period.application_deadline ? String(period.application_deadline).slice(0, 10) : "",
      start_time: period.start_time ? String(period.start_time).slice(0, 5) : "",
      end_time: period.end_time ? String(period.end_time).slice(0, 5) : "",
      days_text: textFromList(period.days_of_week),
    });
    setPeriodFormOpen(true);
  }

  async function savePeriod(event) {
    event.preventDefault();
    if (!periodForm.course_id || !periodForm.title.trim()) return setNotice("Kurs ve dönem adı zorunludur.");
    setBusy(true);
    try {
      const payload = { ...periodForm, days_of_week: listFromText(periodForm.days_text) };
      await api(periodEditingId ? `/periods/${periodEditingId}` : "/periods", {
        method: periodEditingId ? "PUT" : "POST",
        body: JSON.stringify(payload),
      });
      setNotice(periodEditingId ? "Dönem güncellendi." : "Dönem oluşturuldu.");
      setPeriodFormOpen(false);
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deletePeriodPermanently(period) {
    const answer = window.prompt(
      `${period.title} kalıcı olarak silinecek.\n\n` +
        "Bağlı başvuru veya kursiyer varsa sistem silmeye izin vermez.\n" +
        "Devam etmek için SİL yazın.",
    );

    if (
      String(answer || "")
        .trim()
        .toLocaleUpperCase("tr-TR") !== "SİL"
    ) {
      return;
    }

    setBusy(true);

    try {
      await api(`/periods/${period.id}/permanent`, {
        method: "DELETE",
      });

      setNotice("Dönem / grup / takım kalıcı olarak silindi.");
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function generateSessions(period) {
    if (!window.confirm(`${period.title} için seçilen günlerde ders / antrenman programı oluşturulsun mu?`)) return;
    setBusy(true);
    try {
      const course = courses.find((item) => String(item.id) === String(period.course_id));
      const type = course?.program_type === "sport" ? "training" : "lesson";
      const json = await api(`/periods/${period.id}/generate-sessions`, {
        method: "POST",
        body: JSON.stringify({ session_type: type, title: period.auto_session_title || "" }),
      });
      setNotice(json.message || `${json.created || 0} program kaydı oluşturuldu.`);
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  function openApplicationAction(application, status = "reviewing") {
    const course = courses.find((item) => String(item.id) === String(application.course_id));
    const period = periods.find((item) => String(item.id) === String(application.period_id));
    setSelectedApplication(application);
    setApplicationAction({
      status,
      admin_note: application.admin_note || "",
      rejection_reason: "",
      user_message: "",
      agreed_price: period?.price || course?.sale_price || course?.base_price || "",
      discount_amount: "",
      scholarship_amount: "",
      installment_count: period?.installment_count || course?.installment_count || 1,
    });
    setApplicationActionOpen(true);
  }

  async function saveApplicationAction(event) {
    event.preventDefault();
    if (!selectedApplication) return;
    setBusy(true);
    try {
      await api(`/applications/${selectedApplication.id}/status`, {
        method: "PATCH",
        body: JSON.stringify(applicationAction),
      });
      setNotice(applicationAction.status === "approved" ? "Başvuru onaylandı, kursiyer ve finans hesabı oluşturuldu." : "Başvuru durumu güncellendi.");
      setApplicationActionOpen(false);
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteApplicationPermanently(application) {
    const answer = window.prompt(
      `${application.full_name} başvurusu kalıcı olarak silinecek.\n\n` +
        "Devam etmek için SİL yazın.",
    );

    if (
      String(answer || "")
        .trim()
        .toLocaleUpperCase("tr-TR") !== "SİL"
    ) {
      return;
    }

    setBusy(true);

    try {
      await api(`/applications/${application.id}/permanent`, {
        method: "DELETE",
      });

      setNotice("Başvuru kalıcı olarak silindi.");
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function openStudentDetail(enrollment) {
    setSelectedEnrollment(enrollment);
    setStudentDetailOpen(true);
    setStudentDetail(null);
    try {
      const json = await api(`/enrollments/${enrollment.id}`);
      setStudentDetail(json.data || null);
    } catch (err) {
      setNotice(err.message);
    }
  }

  async function deleteEnrollmentPermanently(enrollment) {
    const answer = window.prompt(
      `${enrollment.full_name} kaydı kalıcı olarak silinecek.\n\n` +
        "Ödeme, yoklama, değerlendirme veya sertifika geçmişi varsa sistem silmeye izin vermez.\n" +
        "Devam etmek için SİL yazın.",
    );

    if (
      String(answer || "")
        .trim()
        .toLocaleUpperCase("tr-TR") !== "SİL"
    ) {
      return;
    }

    setBusy(true);

    try {
      await api(`/enrollments/${enrollment.id}/permanent`, {
        method: "DELETE",
      });

      setStudentDetailOpen(false);
      setNotice("Kursiyer / sporcu kaydı kalıcı olarak silindi.");
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  function openPayment(enrollment) {
    setSelectedEnrollment(enrollment);
    setPaymentForm({
      amount: enrollment.remaining_amount || "",
      payment_method: "cash",
      payment_date: new Date().toISOString().slice(0, 10),
      reference_no: "",
      note: "",
    });
    setPaymentOpen(true);
  }

  async function savePayment(event) {
    event.preventDefault();
    if (!selectedEnrollment || Number(paymentForm.amount || 0) <= 0) return setNotice("Geçerli bir tahsilat tutarı girin.");
    setBusy(true);
    try {
      await api(`/enrollments/${selectedEnrollment.id}/payments`, {
        method: "POST",
        body: JSON.stringify(paymentForm),
      });
      setNotice("Tahsilat kaydedildi ve account.js hesabına işlendi.");
      setPaymentOpen(false);
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function updatePaymentNotice(notice, status) {
    const text = status === "approved" ? "Bu ödeme bildirimi onaylanıp account.js ve kasaya işlensin mi?" : "Ödeme bildirimi reddedilsin mi?";
    if (!window.confirm(text)) return;
    setBusy(true);
    try {
      await api(`/payment-notices/${notice.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      setNotice(status === "approved" ? "Ödeme onaylandı; öğrenci hesabı ve kasa kaydı oluştu." : "Ödeme bildirimi reddedildi.");
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deletePaymentNoticePermanently(notice) {
    const answer = window.prompt(
      `${notice.notice_no || "Ödeme bildirimi"} kalıcı olarak silinecek.\n\n` +
        "Onaylanmış bildirimler silinemez.\n" +
        "Devam etmek için SİL yazın.",
    );

    if (
      String(answer || "")
        .trim()
        .toLocaleUpperCase("tr-TR") !== "SİL"
    ) {
      return;
    }

    setBusy(true);

    try {
      await api(`/payment-notices/${notice.id}/permanent`, {
        method: "DELETE",
      });

      setNotice("Ödeme bildirimi kalıcı olarak silindi.");
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  function openCertificate(enrollment) {
    setSelectedEnrollment(enrollment);
    setCertificateForm({
      title: `${enrollment.course_title} Başarı Sertifikası`,
      issue_date: new Date().toISOString().slice(0, 10),
      file_url: "",
    });
    setCertificateOpen(true);
  }

  async function saveCertificate(event) {
    event.preventDefault();
    if (!selectedEnrollment) return;
    setBusy(true);
    try {
      await api(`/enrollments/${selectedEnrollment.id}/certificate`, {
        method: "POST",
        body: JSON.stringify(certificateForm),
      });
      setNotice("Sertifika oluşturuldu ve kursiyere bildirildi.");
      setCertificateOpen(false);
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  function openNewInstructor() {
    setInstructorEditingId(null);
    setInstructorForm(EMPTY_INSTRUCTOR);
    setInstructorFormOpen(true);
  }

  function editInstructor(instructor) {
    setInstructorEditingId(instructor.id);
    setInstructorForm({
      ...EMPTY_INSTRUCTOR,
      ...instructor,
      specialties_text: textFromList(instructor.specialties),
    });
    setInstructorFormOpen(true);
  }

  async function saveInstructor(event) {
    event.preventDefault();

    if (!instructorForm.full_name.trim()) {
      return setNotice("Eğitmen adı zorunludur.");
    }

    setBusy(true);

    try {
      await api(
        instructorEditingId
          ? `/instructors/${instructorEditingId}`
          : "/instructors",
        {
          method: instructorEditingId ? "PUT" : "POST",
          body: JSON.stringify({
            ...instructorForm,
            specialties: listFromText(
              instructorForm.specialties_text,
            ),
          }),
        },
      );

      setNotice(
        instructorEditingId
          ? "Eğitmen / antrenör güncellendi."
          : "Eğitmen / antrenör eklendi.",
      );

      setInstructorFormOpen(false);
      setInstructorEditingId(null);
      setInstructorForm(EMPTY_INSTRUCTOR);
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteInstructorPermanently(instructor) {
    const answer = window.prompt(
      `${instructor.full_name} kalıcı olarak silinecek.\n\n` +
        "Bağlı dersler silinmez; eğitmen alanı boş bırakılır.\n" +
        "Devam etmek için SİL yazın.",
    );

    if (
      String(answer || "")
        .trim()
        .toLocaleUpperCase("tr-TR") !== "SİL"
    ) {
      return;
    }

    setBusy(true);

    try {
      await api(`/instructors/${instructor.id}/permanent`, {
        method: "DELETE",
      });

      setNotice("Eğitmen / antrenör kalıcı olarak silindi.");
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  function openNewSession() {
    setSessionEditingId(null);
    setSessionForm(EMPTY_SESSION);
    setSessionFormOpen(true);
  }

  function editSession(session) {
    setSessionEditingId(session.id);
    setSessionForm({
      ...EMPTY_SESSION,
      ...session,
      course_id: session.course_id || "",
      period_id: session.period_id || "",
      instructor_id: session.instructor_id || "",
      session_date: session.session_date
        ? String(session.session_date).slice(0, 10)
        : "",
      start_time: session.start_time
        ? String(session.start_time).slice(0, 5)
        : "",
      end_time: session.end_time
        ? String(session.end_time).slice(0, 5)
        : "",
      attendance_required:
        session.attendance_required !== false,
    });
    setSessionFormOpen(true);
  }

  async function saveSession(event) {
    event.preventDefault();

    if (
      !sessionForm.course_id ||
      !sessionForm.title.trim() ||
      !sessionForm.session_date
    ) {
      return setNotice(
        "Program, ders / antrenman adı ve tarih zorunludur.",
      );
    }

    if (
      sessionForm.attendance_required !== false &&
      !sessionForm.period_id
    ) {
      return setNotice(
        "Yoklama alınacak program için sınıf / grup / takım seçmelisiniz.",
      );
    }

    setBusy(true);

    try {
      await api(
        sessionEditingId
          ? `/sessions/${sessionEditingId}`
          : "/sessions",
        {
          method: sessionEditingId ? "PUT" : "POST",
          body: JSON.stringify(sessionForm),
        },
      );

      setNotice(
        sessionEditingId
          ? "Ders / antrenman güncellendi."
          : "Ders / antrenman programı oluşturuldu.",
      );

      setSessionFormOpen(false);
      setSessionEditingId(null);
      setSessionForm(EMPTY_SESSION);
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteSessionPermanently(session) {
    const answer = window.prompt(
      `${session.title} kalıcı olarak silinecek.\n\n` +
        "Bu derse ait yoklamalar da kalıcı olarak silinir.\n" +
        "Devam etmek için SİL yazın.",
    );

    if (
      String(answer || "")
        .trim()
        .toLocaleUpperCase("tr-TR") !== "SİL"
    ) {
      return;
    }

    setBusy(true);

    try {
      await api(`/sessions/${session.id}/permanent`, {
        method: "DELETE",
      });

      setNotice(
        "Ders / antrenman ve bağlı yoklamalar kalıcı olarak silindi.",
      );
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

function openManualEnrollment() {
  setManualEnrollmentForm(
    EMPTY_MANUAL_ENROLLMENT,
  );

  setManualEnrollmentOpen(true);
}

async function saveManualEnrollment(event) {
  event.preventDefault();

  if (
    !manualEnrollmentForm.course_id ||
    !manualEnrollmentForm.period_id ||
    !manualEnrollmentForm.full_name.trim()
  ) {
    return setNotice(
      "Program, sınıf / takım ve ad soyad zorunludur.",
    );
  }

  setBusy(true);

  try {
    await api("/enrollments/manual", {
      method: "POST",
      body: JSON.stringify(
        manualEnrollmentForm,
      ),
    });

    setNotice(
      "Kursiyer / sporcu sınıf veya takıma kaydedildi.",
    );

    setManualEnrollmentOpen(false);

    setManualEnrollmentForm(
      EMPTY_MANUAL_ENROLLMENT,
    );

    await loadAll(true);
  } catch (err) {
    setNotice(err.message);
  } finally {
    setBusy(false);
  }
}


async function openAttendance(session) {
  setAttendanceSession(session);
  setAttendanceRows([]);
  setAttendanceOpen(true);

  try {
    const json = await api(
      `/sessions/${session.id}/attendance`,
    );

    setAttendanceRows(
      (json.data || []).map((item) => ({
        ...item,

        status:
          item.attendance_status ||
          "unmarked",

        note:
          item.note || "",

        coach_note:
          item.coach_note || "",

        absence_reason:
          item.absence_reason || "",

        performance_score:
          item.performance_score ?? "",
      })),
    );
  } catch (err) {
    setAttendanceOpen(false);

    setNotice(
      err.message ||
        "Yoklama listesi alınamadı.",
    );
  }
}

async function saveAttendance() {





    if (!attendanceSession) return;
    setBusy(true);
    try {
      await api(`/sessions/${attendanceSession.id}/attendance`, {
        method: "POST",
        body: JSON.stringify({ records: attendanceRows.map((item) => ({
          enrollment_id: item.enrollment_id,
          status: item.status,
          note: item.note,
          coach_note: item.coach_note,
          absence_reason: item.absence_reason,
          performance_score: item.performance_score,
        })) }),
      });
      setNotice("Yoklama kaydedildi.");
      setAttendanceOpen(false);
      await loadAll(true);
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveAssessment(event) {
    event.preventDefault();
    const enrollmentId = studentDetail?.id || selectedEnrollment?.id;
    if (!enrollmentId || !assessmentForm.title.trim()) return setNotice("Değerlendirme başlığı zorunludur.");
    setBusy(true);
    try {
      await api(`/enrollments/${enrollmentId}/assessments`, {
        method: "POST",
        body: JSON.stringify({
          ...assessmentForm,
          score: assessmentForm.score === "" ? null : Number(assessmentForm.score),
          max_score: assessmentForm.max_score === "" ? null : Number(assessmentForm.max_score),
        }),
      });
      const json = await api(`/enrollments/${enrollmentId}`);
      setStudentDetail(json.data || null);
      setAssessmentForm({ title: "", assessment_type: "progress", score: "", max_score: "100", grade: "", note: "", visible_to_student: true });
      setNotice("Gelişim / performans değerlendirmesi kaydedildi.");
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteAssessmentPermanently(assessment) {
    if (
      !window.confirm(
        `${assessment.title} değerlendirmesi kalıcı olarak silinsin mi?`,
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      await api(`/assessments/${assessment.id}/permanent`, {
        method: "DELETE",
      });

      const enrollmentId =
        studentDetail?.id || selectedEnrollment?.id;

      if (enrollmentId) {
        const json = await api(`/enrollments/${enrollmentId}`);
        setStudentDetail(json.data || null);
      }

      setNotice("Değerlendirme kalıcı olarak silindi.");
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function refreshReport() {
    setBusy(true);
    try {
      const [json, fullJson] = await Promise.all([
        api(`/reports/summary?start=${reportDates.start}&end=${reportDates.end}`),
        api(`/reports/full?start=${reportDates.start}&end=${reportDates.end}`),
      ]);
      setReport(json.data || {});
      setFullReport(fullJson.data || {});
      setNotice("Rapor güncellendi.");
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  }

  function exportCsv() {
    const rows = [];
    const section = (title, headers, data) => {
      rows.push([title]);
      rows.push(headers);
      rows.push(...data);
      rows.push([]);
    };
    section("KATILIMCI / SPORCU FİNANS LİSTESİ", ["Kayıt No", "Katılımcı", "Program", "Grup", "Toplam", "Ödenen", "Kalan", "Ödeme Durumu"],
      filteredEnrollments.map((item) => [item.enrollment_no, item.full_name, item.course_title, item.period_title || item.team_name || "", item.agreed_price, item.paid_amount, item.remaining_amount, STATUS_LABELS[item.payment_status] || item.payment_status]));
    section("PROGRAM PORTFÖYÜ", ["Program Türü", "Toplam", "Yayında"],
      (fullReport.programs || []).map((item) => [programTypeLabel(item.program_type), item.total, item.published]));
    section("DÖNEM / TAKIM DOLULUK", ["Program", "Grup / Takım", "Yaş Grubu", "Aktif", "Kontenjan", "Kalan"],
      (fullReport.periods || []).map((item) => [item.title_tr, item.team_name || item.title, item.age_group || "", item.active_students, item.capacity, item.remaining]));
    section("AYLIK TAHSİLAT", ["Ay", "Ödeme Sayısı", "Tahsilat"],
      (fullReport.monthly || []).map((item) => [item.month, item.payment_count, item.total]));
    section("YOKLAMA DAĞILIMI", ["Durum", "Adet"],
      (fullReport.attendance || []).map((item) => [STATUS_LABELS[item.status] || item.status, item.total]));
    section("PROGRAM BAZLI KATILIM", ["Program", "Tür", "İşaretlenen", "Katılan", "Katılım Oranı"],
      (fullReport.attendance_by_program || []).map((item) => [item.title_tr, programTypeLabel(item.program_type), item.marked, item.attended, item.attendance_rate]));
    section("GECİKEN TAKSİTLER", ["Katılımcı", "Kayıt No", "Program", "Vade", "Taksit", "Tutar", "Ödenen"],
      (fullReport.overdue || []).map((item) => [item.full_name, item.enrollment_no, item.course_title, item.due_date, item.installment_no, item.amount, item.paid_amount]));
    const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replaceAll('"', '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `mesyat-tum-program-raporu-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }


  function renderHeader() {
    const collectionRate = Math.round(
      Number(stats.total_collected || 0) /
        Math.max(
          1,
          Number(stats.total_collected || 0) +
            Number(stats.total_receivable || 0)
        ) *
        100
    );

    return (
      <header className="hotel-course-header">
        <div className="hotel-course-brand">
          <div className="hotel-course-logo" aria-hidden="true">🎓</div>
          <div>
            <h1>MesYat Eğitim & Spor</h1>
            <p>
              Kurs, yaşam koçluğu, spor akademisi, öğrenci, yoklama,
              tahsilat ve rapor yönetimi
            </p>
          </div>
        </div>

        <div className="hotel-course-actions">
          <span className="hotel-live-chip">
            <i /> {stats.active_students || 0} AKTİF KATILIMCI
          </span>
          <span className="hotel-rate-chip">%{collectionRate} TAHSİLAT</span>
          <button
            className="hotel-header-button secondary"
            type="button"
            onClick={() => loadAll()}
            disabled={loading || busy}
          >
            ↻ YENİLE
          </button>
          <button
            className="hotel-header-button primary"
            type="button"
            onClick={openNewCourse}
          >
            + YENİ PROGRAM
          </button>
        </div>
      </header>
    );
  }

  function renderSidebar() {
    return (
      <nav className="hotel-course-tabs" aria-label="Kurs yönetim bölümleri">
        {NAV_ITEMS.map(([key, label, icon]) => (
          <button
            key={key}
            type="button"
            className={activeTab === key ? "active" : ""}
            onClick={() => {
              setActiveTab(key);
              setMobileNav(false);
              resetFilters();
            }}
          >
            <span className="hotel-tab-icon" aria-hidden="true">{icon}</span>
            <b>{label}</b>
          </button>
        ))}
      </nav>
    );
  }

  function renderToolbar(statusOptions = []) {
    return (
      <div className="panel toolbar">
        <div className="searchbox">
          <span>⌕</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Kurs, kişi, telefon veya kayıt no ara..." />
        </div>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="all">Tüm durumlar</option>
          {statusOptions.map((status) => <option key={status} value={status}>{STATUS_LABELS[status] || status}</option>)}
        </select>
        {(search || statusFilter !== "all") && <button type="button" className="button ghost compact" onClick={resetFilters}>Temizle</button>}
      </div>
    );
  }

  function renderOverview() {
    const collectionRate = Math.round(
      Number(stats.total_collected || 0) /
        Math.max(
          1,
          Number(stats.total_collected || 0) +
            Number(stats.total_receivable || 0)
        ) *
        100
    );

    const attendanceRate = Math.round(Number(stats.attendance_rate || 0));
    const totalCourses = Number(stats.total_courses || courses.length || 0);
    const todaySessions = Number(
      stats.sessions_today ||
        sessions.filter((item) =>
          String(item.session_date || "").startsWith(
            new Date().toISOString().slice(0, 10)
          )
        ).length
    );

    return (
      <>
        <section className="hotel-quick-strip">
          <label>
            <span>Hızlı İşlem</span>
            <select
              value=""
              onChange={(event) => {
                const value = event.target.value;
                if (value === "course") openNewCourse();
                if (value === "period") {
                  setPeriodForm({ ...EMPTY_PERIOD });
                  setPeriodEditingId(null);
                  setPeriodFormOpen(true);
                }
                if (value === "session") {
                  setSessionForm({ ...EMPTY_SESSION });
                  setSessionFormOpen(true);
                }
              }}
            >
              <option value="">İşlem seçin</option>
              <option value="course">Yeni kurs / program</option>
              <option value="period">Yeni dönem / takım</option>
              <option value="session">Yeni ders / antrenman</option>
            </select>
          </label>

          <label>
            <span>Program Görünümü</span>
            <select
              value={activeTab}
              onChange={(event) => {
                setActiveTab(event.target.value);
                resetFilters();
              }}
            >
              {NAV_ITEMS.map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </label>

          <label>
            <span>Kurum Portföyü</span>
            <input
              value={`${totalCourses} program · ${stats.active_periods || 0} aktif dönem`}
              readOnly
            />
          </label>
        </section>

        <section className="hotel-metric-grid">
          <Metric label="Toplam Program" value={totalCourses} detail={`${stats.published_courses || 0} yayında`} tone="navy" />
          <Metric label="Aktif Dönem / Takım" value={stats.active_periods || 0} detail="Eğitim ve spor grupları" tone="orange" />
          <Metric label="Bekleyen Başvuru" value={stats.pending_applications || 0} detail="İnceleme bekliyor" tone="purple" />
          <Metric label="Aktif Öğrenci / Sporcu" value={stats.active_students || 0} detail="Kayıtlı katılımcı" tone="green" />
          <Metric label="Bugünkü Ders / Antrenman" value={todaySessions} detail="Günlük program" tone="blue" />
          <Metric label="Toplam Tahsilat" value={money(stats.total_collected || 0)} detail="Kasa + account.js" tone="green" />
          <Metric label="Açık Bakiye" value={money(stats.total_receivable || 0)} detail={`${stats.overdue_installments || 0} gecikmiş taksit`} tone="red" />
          <Metric label="Katılım Oranı" value={`%${attendanceRate}`} detail="İşaretlenen yoklamalar" tone="purple" />
        </section>

        <div className="hotel-overview-layout">
          <section className="panel hotel-recent-panel">
            <SectionTitle
              eyebrow="SON HAREKETLER"
              title="Son Başvurular"
              action={
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setActiveTab("applications")}
                >
                  Tümünü gör
                </button>
              }
            />
            <div className="activity-list hotel-activity-list">
              {(dashboard.latest_applications || []).length ? (
                dashboard.latest_applications.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className="activity-row"
                    onClick={() =>
                      openApplicationAction(
                        item,
                        item.status === "new" ? "reviewing" : item.status
                      )
                    }
                  >
                    <Avatar name={item.full_name} />
                    <span>
                      <b>{item.full_name}</b>
                      <small>
                        {item.course_title} · {item.period_title || "Dönem seçilmedi"}
                        <br />
                        {dateTimeText(item.created_at)}
                      </small>
                    </span>
                    <Status status={item.status} />
                  </button>
                ))
              ) : (
                <Empty text="Henüz başvuru kaydı yok." />
              )}
            </div>
          </section>

          <aside className="hotel-summary-panel">
            <div className="hotel-summary-title">
              <span>ÖZET</span>
              <b>{totalCourses} Toplam Program</b>
            </div>
            <h3>Operasyon Başarı Oranı</h3>

            <div
              className="hotel-summary-ring"
              style={{ "--ring-value": `${collectionRate * 3.6}deg` }}
            >
              <div>
                <span>TAHSİLAT</span>
                <strong>{collectionRate.toFixed(1)}%</strong>
              </div>
            </div>

            <div className="hotel-summary-income">
              <span>Toplam Tahsilat</span>
              <strong>{money(stats.total_collected || 0)}</strong>
            </div>

            <div className="hotel-summary-rows">
              <div><span>📚 Yayındaki Program</span><b>{stats.published_courses || 0}</b></div>
              <div><span>👥 Aktif Katılımcı</span><b>{stats.active_students || 0}</b></div>
              <div><span>📥 Bekleyen Başvuru</span><b>{stats.pending_applications || 0}</b></div>
              <div><span>⚠️ Geciken Taksit</span><b>{stats.overdue_installments || 0}</b></div>
              <div><span>✅ Katılım Oranı</span><b>%{attendanceRate}</b></div>
            </div>
          </aside>
        </div>

        <div className="hotel-lower-grid">
          <section className="panel">
            <SectionTitle eyebrow="TAKVİM" title="Yaklaşan Ders ve Antrenmanlar" />
            <div className="schedule-list">
              {(dashboard.upcoming_sessions || []).length ? (
                dashboard.upcoming_sessions.slice(0, 6).map((item) => (
                  <article key={item.id}>
                    <div className="date-box">
                      <strong>{new Date(item.session_date).getDate()}</strong>
                      <span>{new Intl.DateTimeFormat("tr-TR", { month: "short" }).format(new Date(item.session_date))}</span>
                    </div>
                    <span>
                      <b>{item.title}</b>
                      <small>{item.course_title} · {String(item.start_time || "").slice(0, 5)}</small>
                    </span>
                    <Status status={item.status} />
                  </article>
                ))
              ) : (
                <Empty text="Yaklaşan ders veya antrenman yok." />
              )}
            </div>
          </section>

          <section className="panel">
            <SectionTitle eyebrow="FİNANS TAKİBİ" title="Yaklaşan ve Geciken Taksitler" />
            <div className="due-list">
              {(dashboard.due_installments || []).length ? (
                dashboard.due_installments.slice(0, 6).map((item) => (
                  <article key={item.id}>
                    <span>
                      <b>{item.full_name}</b>
                      <small>{item.course_title} · {dateText(item.due_date)}</small>
                    </span>
                    <strong>{money(Number(item.amount || 0) - Number(item.paid_amount || 0), item.currency)}</strong>
                  </article>
                ))
              ) : (
                <Empty text="Bekleyen taksit kaydı yok." />
              )}
            </div>
          </section>
        </div>
      </>
    );
  }

  function renderCourses() {
    return (
      <>
        <PageTitle eyebrow="İÇERİK YÖNETİMİ" title="Kurslar & Programlar" description="Her kurum sınırsız yaşam koçluğu, mesleki eğitim, spor okulu, takım, kamp ve özel ders programı ekleyebilir." action={<button className="button primary" type="button" onClick={openNewCourse}>Yeni Kurs / Program</button>} />
        {renderToolbar(["draft", "published", "paused", "completed", "archived"])}
        <div className="course-card-grid">
          {filteredCourses.length ? filteredCourses.map((course) => (
            <article key={course.id} className="course-card">
              <div className="course-cover" style={course.cover_image ? { backgroundImage: `linear-gradient(180deg, transparent, rgba(5,27,43,.78)), url(${course.cover_image})` } : undefined}>
                {!course.cover_image && <div className="cover-placeholder">{String(course.title_tr || "K").charAt(0)}</div>}
                <div className="cover-top"><Status status={course.status} /><span className="course-code">{course.code}</span></div>
                <div className="cover-bottom"><span>{`${programTypeLabel(course.program_type)}${course.sport_branch ? ` · ${course.sport_branch}` : ""}`}</span><h3>{course.title_tr}</h3></div>
              </div>
              <div className="course-body">
                <p>{course.short_description_tr || "Kurs açıklaması henüz eklenmedi."}</p>
                <div className="course-meta">
                  <span><b>{course.period_count || 0}</b>Dönem</span>
                  <span><b>{course.application_count || 0}</b>Başvuru</span>
                  <span><b>{course.student_count || 0}</b>Katılımcı</span>
                </div>
                <div className="price-row">
                  <span><small>Satış fiyatı</small><b>{course.free ? "Ücretsiz" : money(course.sale_price || course.base_price, course.currency)}</b></span>
                  <span className="location">{course.city || "Konum yok"}{course.district ? ` / ${course.district}` : ""}</span>
                </div>
                <div className="card-actions">
                  <button type="button" onClick={() => editCourse(course)}>Düzenle</button>
                  <button type="button" onClick={() => openNewPeriod(course.id)}>Dönem / Grup Aç</button>
                  <button type="button" onClick={() => archiveCourse(course)}>Arşivle</button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => deleteCoursePermanently(course)}
                  >
                    Kalıcı Sil
                  </button>
                </div>
              </div>
            </article>
          )) : <div className="full-empty"><Empty text="Filtreye uygun kurs / program bulunamadı." /></div>}
        </div>
      </>
    );
  }

  function renderPeriods() {
    return (
      <>
        <PageTitle eyebrow="TAKVİM & KONTENJAN" title="Dönemler, Takımlar & Gruplar" description="Aynı program için yaş grubu, takım, hafta içi, hafta sonu, sezon veya online grup açın; takvimi otomatik üretin." action={<button className="button primary" type="button" onClick={() => openNewPeriod()}>Yeni Dönem</button>} />
        {renderToolbar(["planned", "open", "full", "started", "completed", "cancelled"])}
        <section className="panel table-panel">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Dönem</th><th>Tarih</th><th>Saat</th><th>Kontenjan</th><th>Fiyat</th><th>Durum</th><th></th></tr></thead>
              <tbody>
                {filteredPeriods.length ? filteredPeriods.map((period) => (
                  <tr key={period.id}>
                    <td><b>{period.title}</b><small>{period.course_title}{period.team_name ? ` · ${period.team_name}` : ""}{period.age_group ? ` · ${period.age_group}` : ""}</small></td>
                    <td>{dateText(period.start_date)}<small>{dateText(period.end_date)} tarihine kadar</small></td>
                    <td>{String(period.start_time || "").slice(0, 5)} – {String(period.end_time || "").slice(0, 5)}<small>{Array.isArray(period.days_of_week) ? period.days_of_week.join(", ") : ""}</small></td>
                    <td><b>{period.enrolled_count || 0} / {period.capacity || "∞"}</b><small>{period.remaining_capacity || 0} boş yer</small></td>
                    <td>{money(period.price, period.currency)}<small>{period.installment_count || 1} taksit</small></td>
                    <td><Status status={period.status} /></td>
                    <td className="right">
                      <div className="row-actions">
                        <button
                          type="button"
                          className="table-action"
                          onClick={() => editPeriod(period)}
                        >
                          Düzenle
                        </button>
                        <button
                          type="button"
                          className="button primary compact"
                          onClick={() => generateSessions(period)}
                        >
                          Programı Üret
                        </button>
                        <button
                          type="button"
                          className="danger"
                          onClick={() => deletePeriodPermanently(period)}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : <tr><td colSpan="7"><Empty text="Dönem bulunamadı." /></td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </>
    );
  }

  function renderApplications() {
    return (
      <>
        <PageTitle eyebrow="KAYIT MERKEZİ" title="Başvurular" description="Başvuruyu inceleyin, belge isteyin, ön onay verin veya onaylayarak otomatik kursiyer ve finans hesabı oluşturun." />
        {renderToolbar(["new", "reviewing", "document_pending", "pre_approved", "payment_pending", "approved", "rejected", "cancelled"])}
        <section className="panel table-panel">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Başvuru Sahibi</th><th>Kurs / Dönem</th><th>İletişim</th><th>Başvuru</th><th>Durum</th><th>İşlemler</th></tr></thead>
              <tbody>
                {filteredApplications.length ? filteredApplications.map((item) => (
                  <tr key={item.id}>
                    <td><div className="person-cell"><Avatar name={item.full_name} /><span><b>{item.full_name}</b><small>{item.application_no}</small></span></div></td>
                    <td><b>{item.course_title}</b><small>{item.period_title || "Dönem seçilmedi"}</small></td>
                    <td>{item.phone || "—"}<small>{item.email || "E-posta yok"}</small></td>
                    <td>{dateTimeText(item.created_at)}<small>{item.city || ""} {item.district || ""}</small></td>
                    <td><Status status={item.status} /></td>
                    <td>
                      <div className="row-actions">
                        {!item.enrollment_id && (
                          <button
                            type="button"
                            className="success"
                            onClick={() =>
                              openApplicationAction(item, "approved")
                            }
                          >
                            Onayla
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            openApplicationAction(
                              item,
                              item.status === "new"
                                ? "reviewing"
                                : item.status,
                            )
                          }
                        >
                          İncele
                        </button>

                        {!item.enrollment_id && (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                openApplicationAction(item, "rejected")
                              }
                            >
                              Reddet
                            </button>

                            <button
                              type="button"
                              className="danger"
                              onClick={() =>
                                deleteApplicationPermanently(item)
                              }
                            >
                              Sil
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : <tr><td colSpan="6"><Empty text="Başvuru bulunamadı." /></td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </>
    );
  }

  function renderStudents() {
    return (
      <>


      <PageTitle
  eyebrow="ÖĞRENCİ YÖNETİMİ"
  title="Kursiyerler & Sporcular"
  description="Uygulamadan onaylanan kişiler otomatik gelir. Yüz yüze kayıt olan kursiyer veya sporcuları panelden de ekleyebilirsiniz."
  action={
    <button
      type="button"
      className="button primary"
      onClick={openManualEnrollment}
    >
      Manuel Kursiyer / Sporcu Ekle
    </button>
  }
/>



        {renderToolbar(["active", "completed", "cancelled", "unpaid", "partial", "paid"])}
        <div className="student-grid">
          {filteredEnrollments.length ? filteredEnrollments.map((item) => (
            <article key={item.id} className="student-card">
              <div className="student-head">
                <Avatar name={item.full_name} large />
                <div><h3>{item.full_name}</h3><p>{item.course_title}</p><small>{item.enrollment_no}</small></div>
                <Status status={item.status} />
              </div>
              <div className="student-finance">
                <span><small>Toplam</small><b>{money(item.agreed_price, item.currency)}</b></span>
                <span><small>Ödenen</small><b className="green-text">{money(item.paid_amount, item.currency)}</b></span>
                <span><small>Kalan</small><b className="orange-text">{money(item.remaining_amount, item.currency)}</b></span>
              </div>
              <div className="balance-bar"><i style={{ width: `${Math.min(100, Number(item.paid_amount || 0) / Math.max(1, Number(item.agreed_price || 0) - Number(item.discount_amount || 0) - Number(item.scholarship_amount || 0)) * 100)}%` }} /></div>
              <div className="student-info"><span>{item.phone || "Telefon yok"}</span><span>{item.period_title || "Dönem yok"}</span></div>
              <div className="card-actions">
                <button
                  type="button"
                  onClick={() => openStudentDetail(item)}
                >
                  Detay
                </button>

                <button
                  type="button"
                  className="success"
                  onClick={() => openPayment(item)}
                >
                  Tahsilat
                </button>

                <button
                  type="button"
                  onClick={() => openCertificate(item)}
                >
                  Sertifika
                </button>

                <button
                  type="button"
                  className="danger"
                  onClick={() => deleteEnrollmentPermanently(item)}
                >
                  Sil
                </button>
              </div>
            </article>
          )) : <div className="full-empty"><Empty text="Kursiyer bulunamadı." /></div>}
        </div>
      </>
    );
  }

  function renderFinance() {
    const collected = filteredEnrollments.reduce((sum, item) => sum + Number(item.paid_amount || 0), 0);
    const remaining = filteredEnrollments.reduce((sum, item) => sum + Number(item.remaining_amount || 0), 0);
    return (
      <>
        <PageTitle eyebrow="ACCOUNT.JS ENTEGRASYONU" title="Tahsilat, Account.js & Kasa Merkezi" description="Öğrencinin borç–ödeme–kalan hesabı account.js üzerinde; gerçek tahsilat aynı anda mevcut kasada Kurs / Program geliri olarak izlenir." action={<button type="button" className="button ghost" onClick={exportCsv}>CSV İndir</button>} />
        <section className="metric-grid compact-grid">
          <Metric label="Listelenen Tahsilat" value={money(collected)} detail={`${filteredEnrollments.length} kursiyer`} tone="green" />
          <Metric label="Açık Alacak" value={money(remaining)} detail={`${receivableRows.length} borçlu kursiyer`} tone="orange" />
          <Metric label="Geciken Taksit" value={stats.overdue_installments || 0} detail="Takip gerekiyor" tone="red" />
          <Metric label="Tahsilat Oranı" value={`${Math.round(collected / Math.max(1, collected + remaining) * 100)}%`} detail="Canlı mutabakat" tone="blue" />
        </section>
        {renderToolbar(["unpaid", "partial", "paid", "active", "completed"])}
        <section className="panel table-panel">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Kursiyer</th><th>Kurs</th><th>Brüt</th><th>İndirim / Burs</th><th>Tahsilat</th><th>Kalan</th><th>Durum</th><th></th></tr></thead>
              <tbody>
                {filteredEnrollments.length ? filteredEnrollments.map((item) => (
                  <tr key={item.id}>
                    <td><b>{item.full_name}</b><small>{item.enrollment_no}</small></td>
                    <td>{item.course_title}<small>{item.period_title || ""}</small></td>
                    <td>{money(item.agreed_price, item.currency)}</td>
                    <td>{money(Number(item.discount_amount || 0) + Number(item.scholarship_amount || 0), item.currency)}</td>
                    <td className="green-text"><b>{money(item.paid_amount, item.currency)}</b></td>
                    <td className="orange-text"><b>{money(item.remaining_amount, item.currency)}</b></td>
                    <td><Status status={item.payment_status} /></td>
<td className="right">
  {pendingPaymentEnrollmentIds.has(
    String(item.id)
  ) ? (
    <button
      type="button"
      className="button ghost compact"
      onClick={() => {
        document
          .getElementById(
            "course-payment-notices"
          )
          ?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
      }}
    >
      Bildirimi İncele
    </button>
  ) : (
    <button
      type="button"
      className="button primary compact"
      onClick={() => openPayment(item)}
    >
      Manuel Tahsilat
    </button>
  )}
</td>
                  </tr>
                )) : <tr><td colSpan="8"><Empty text="Finans kaydı bulunamadı." /></td></tr>}
              </tbody>
            </table>
          </div>
        </section>

<section
  id="course-payment-notices"
  className="panel table-panel"
>
  <SectionTitle
    eyebrow="ÖĞRENCİDEN GELEN"
    title="Ödeme Bildirimleri"
    action={
      <Status
        status={
          pendingPaymentNotices.length
            ? "pending"
            : "completed"
        }
        label={
          pendingPaymentNotices.length
            ? `${pendingPaymentNotices.length} bekleyen`
            : "Bekleyen bildirim yok"
        }
      />
    }
  />


          <div className="table-wrap"><table><thead><tr><th>Öğrenci</th><th>Program</th><th>Tutar</th><th>Tarih / Referans</th><th>Durum</th><th></th></tr></thead><tbody>
            {paymentNotices.length ? (
              paymentNotices.map((item) => (
                <tr key={item.id}>
                  <td>
                    <b>{item.full_name}</b>
                    <small>{item.enrollment_no}</small>
                  </td>
                  <td>
                    {item.course_title}
                    <small>{item.period_title || ""}</small>
                  </td>
                  <td>
                    <b>{money(item.amount, item.currency)}</b>
                  </td>
                  <td>
                    {dateText(item.payment_date)}
                    <small>{item.reference_no || item.notice_no}</small>
                  </td>
                  <td>
                    <Status status={item.status} />
                  </td>
                  <td className="right">
                    <div className="row-actions">
                      {item.status === "pending" && (
                        <>
                          <button
                            className="button primary compact"
                            type="button"
                            onClick={() =>
                              updatePaymentNotice(item, "approved")
                            }
                          >
                            Onayla & Kaydet
                          </button>

                          <button
                            className="button ghost compact"
                            type="button"
                            onClick={() =>
                              updatePaymentNotice(item, "rejected")
                            }
                          >
                            Reddet
                          </button>
                        </>
                      )}

                      {item.status !== "approved" && (
                        <button
                          className="danger"
                          type="button"
                          onClick={() =>
                            deletePaymentNoticePermanently(item)
                          }
                        >
                          Sil
                        </button>
                      )}

                      {item.status === "approved" && "—"}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6">
                  <Empty text="Ödeme bildirimi yok." />
                </td>
              </tr>
            )}
          </tbody></table></div>
        </section>
      </>
    );
  }

  function renderSessions() {
    return (
      <>
        <PageTitle
          eyebrow="EĞİTİM OPERASYONU"
          title="Ders, Antrenman, Maç & Yoklama"
          description="Dersleri veya spor antrenmanlarını planlayın; düzenleyin, iptal edin veya yanlış kaydı kalıcı silin."
          action={
            <button
              type="button"
              className="button primary"
              onClick={openNewSession}
            >
              Yeni Ders / Antrenman
            </button>
          }
        />
        <section className="metric-grid compact-grid">
          <Metric label="Bugünkü Program" value={todaySessions.length} detail={`${todaySessions.reduce((sum,item)=>sum+Number(item.student_count||0),0)} katılımcı`} tone="navy" />
          <Metric label="Yoklaması Alınan" value={todaySessions.filter((item)=>Number(item.marked_count||0)>0).length} detail="Bugünkü ders / antrenman" tone="green" />
          <Metric label="Gelen" value={todaySessions.reduce((sum,item)=>sum+Number(item.present_count||0),0)} detail="Katıldı / geç / online" tone="blue" />
          <Metric label="Gelmeyen" value={todaySessions.reduce((sum,item)=>sum+Number(item.absent_count||0),0)} detail="Yok / mazeret / rapor" tone="orange" />
        </section>
        {renderToolbar(["planned", "started", "completed", "cancelled"])}
        <div className="session-grid">
          {sessions.filter((item) => {
            const query = search.toLocaleLowerCase("tr-TR");
            const match = !query || [item.title, item.course_title, item.period_title, item.instructor_name].join(" ").toLocaleLowerCase("tr-TR").includes(query);
            return match && (statusFilter === "all" || item.status === statusFilter);
          }).map((session) => (
            <article key={session.id} className="session-card">
              <div className="session-date"><strong>{new Date(session.session_date).getDate()}</strong><span>{new Intl.DateTimeFormat("tr-TR", { month: "short" }).format(new Date(session.session_date))}</span></div>
              <div className="session-content">
                <div className="session-title-row"><h3>{session.title}</h3><Status status={session.status} /></div>
                <p>{session.course_title} · {session.period_title || "Grup belirtilmedi"} · {sessionTypeLabel(session.session_type)}</p>
                <div className="session-meta"><span>{String(session.start_time || "").slice(0, 5)} – {String(session.end_time || "").slice(0, 5)}</span><span>{session.classroom || session.meeting_url || "Yer belirtilmedi"}</span><span>{session.instructor_name || "Eğitmen atanmadı"}</span></div>
              </div>
              <div className="row-actions">
                <button
                  type="button"
                  onClick={() => editSession(session)}
                >
                  Düzenle
                </button>

                <button
                  type="button"
                  className="button primary compact"
                  onClick={() => openAttendance(session)}
                >
                  Yoklama Al
                </button>

                <button
                  type="button"
                  className="danger"
                  onClick={() => deleteSessionPermanently(session)}
                >
                  Sil
                </button>
              </div>
            </article>
          ))}
          {!sessions.length && <div className="full-empty"><Empty text="Henüz ders / antrenman programı oluşturulmadı." /></div>}
        </div>
      </>
    );
  }

  function renderInstructors() {
    return (
      <>
        <PageTitle
          eyebrow="EĞİTİM KADROSU"
          title="Eğitmenler & Antrenörler"
          description="Eğitmen, yaşam koçu ve antrenör bilgilerini ekleyin, düzenleyin veya kalıcı olarak silin."
          action={
            <button
              type="button"
              className="button primary"
              onClick={openNewInstructor}
            >
              Eğitmen / Antrenör Ekle
            </button>
          }
        />
        <div className="instructor-grid">
          {instructors.length ? instructors.map((item) => (
            <article key={item.id} className="instructor-card">
              <div className="instructor-photo" style={item.photo_url ? { backgroundImage: `url(${item.photo_url})` } : undefined}>{!item.photo_url && String(item.full_name || "E").charAt(0)}</div>
              <div><Status status={item.active ? "active" : "paused"} /><h3>{item.full_name}</h3><p>{item.title || "Eğitmen"}</p><small>{item.email || item.phone || "İletişim bilgisi yok"}</small></div>
              <div className="specialty-list">{(Array.isArray(item.specialties) ? item.specialties : []).slice(0, 4).map((tag) => <span key={tag}>{tag}</span>)}</div>
              <p className="bio">
                {item.biography || "Eğitmen öz geçmişi eklenmedi."}
              </p>

              <div className="card-actions">
                <button
                  type="button"
                  onClick={() => editInstructor(item)}
                >
                  Düzenle
                </button>

                <button
                  type="button"
                  className="danger"
                  onClick={() => deleteInstructorPermanently(item)}
                >
                  Sil
                </button>
              </div>
            </article>
          )) : <div className="full-empty"><Empty text="Henüz eğitmen eklenmedi." /></div>}
        </div>
      </>
    );
  }

  function renderReports() {
    const summary = report.summary || {};
    const finance = fullReport.finance || {};
    return (
      <>
        <PageTitle eyebrow="KURUMSAL ANALİZ" title="Tam Kurs, Spor & Finans Raporları" description="Kurumun istediği kayıt, tahsilat, kasa, alacak, başvuru dönüşümü, doluluk, yoklama, performans ve eğitmen verilerini aynı raporda görün." action={<button type="button" className="button ghost" onClick={() => window.print()}>Yazdır / PDF</button>} />
        <section className="panel report-filter">
          <Field label="Başlangıç"><input type="date" value={reportDates.start} onChange={(event) => setReportDates((old) => ({ ...old, start: event.target.value }))} /></Field>
          <Field label="Bitiş"><input type="date" value={reportDates.end} onChange={(event) => setReportDates((old) => ({ ...old, end: event.target.value }))} /></Field>
          <button type="button" className="button primary" onClick={refreshReport}>Raporu Getir</button>
          <button type="button" className="button ghost" onClick={exportCsv}>CSV İndir</button>
        </section>
        <section className="metric-grid compact-grid">
          <Metric label="Toplam Kayıt" value={summary.enrollment_count || 0} detail={`${summary.active_student_count || 0} aktif`} tone="navy" />
          <Metric label="Brüt Satış" value={money(finance.gross || summary.gross_sales || 0)} detail="Sözleşme / üyelik toplamı" tone="blue" />
          <Metric label="Kasa Tahsilatı" value={money(finance.collected || summary.collected || 0)} detail="Gerçekleşen ödeme" tone="green" />
          <Metric label="Açık Alacak" value={money(finance.receivable || summary.receivable || 0)} detail={`${(fullReport.overdue || []).length} gecikmiş taksit`} tone="orange" />
        </section>
        <div className="report-grid">
          <section className="panel"><SectionTitle eyebrow="PROGRAM TİPLERİ" title="Kurum Portföyü" /><div className="legend-list">{(fullReport.programs || []).map((item)=><div key={item.program_type}><span><i />{programTypeLabel(item.program_type)}</span><b>{item.published} yayında / {item.total} toplam</b></div>)}{!(fullReport.programs||[]).length&&<Empty text="Program verisi yok."/>}</div></section>
          <section className="panel"><SectionTitle eyebrow="YOKLAMA" title="Katılım Dağılımı" /><div className="legend-list">{(fullReport.attendance || []).map((item)=><div key={item.status}><span><i />{STATUS_LABELS[item.status]||item.status}</span><b>{item.total}</b></div>)}{!(fullReport.attendance||[]).length&&<Empty text="Yoklama verisi yok."/>}</div></section>
        </div>
        <section className="panel table-panel"><SectionTitle eyebrow="DÖNEM / TAKIM" title="Doluluk ve Kapasite"/><div className="table-wrap"><table><thead><tr><th>Program</th><th>Grup / Takım</th><th>Tür</th><th>Aktif</th><th>Kontenjan</th><th>Kalan</th></tr></thead><tbody>{(fullReport.periods||[]).map((item)=><tr key={item.id}><td><b>{item.title_tr}</b></td><td>{item.title}<small>{item.team_name||item.age_group||""}</small></td><td>{programTypeLabel(item.program_type)}</td><td>{item.active_students}</td><td>{item.capacity||"Sınırsız"}</td><td>{item.remaining}</td></tr>)}{!(fullReport.periods||[]).length&&<tr><td colSpan="6"><Empty text="Dönem / grup verisi yok."/></td></tr>}</tbody></table></div></section>
        <div className="report-grid">
          <section className="panel"><SectionTitle eyebrow="TAHSİLAT TRENDİ" title="Aylık Gelir"/><div className="bar-list">{(fullReport.monthly||[]).map((item)=>{const max=Math.max(...(fullReport.monthly||[]).map((x)=>Number(x.total||0)),1);return <article key={item.month}><div><b>{item.month}</b><span>{item.payment_count} ödeme</span></div><div className="bar"><i style={{width:`${Math.round(Number(item.total||0)/max*100)}%`}}/></div><strong>{money(item.total)}</strong></article>})}{!(fullReport.monthly||[]).length&&<Empty text="Tahsilat verisi yok."/>}</div></section>
          <section className="panel"><SectionTitle eyebrow="BAŞVURU HUNİSİ" title="Başvurudan Kayda"/><div className="legend-list">{(fullReport.application_funnel||[]).map((item)=><div key={item.status}><span><i />{STATUS_LABELS[item.status]||item.status}</span><b>{item.total}</b></div>)}</div></section>
        </div>
        <div className="report-grid">
          <section className="panel"><SectionTitle eyebrow="PROGRAM BAZLI" title="Katılım Oranları"/><div className="legend-list">{(fullReport.attendance_by_program||[]).map((item)=><div key={item.id}><span><i />{item.title_tr}<small>{programTypeLabel(item.program_type)}</small></span><b>%{item.attendance_rate||0}</b></div>)}{!(fullReport.attendance_by_program||[]).length&&<Empty text="Program katılım verisi yok."/>}</div></section>
          <section className="panel"><SectionTitle eyebrow="KATILIMCI PROFİLİ" title="Demografik Özet"/><div className="student-finance big"><span><small>18 Yaş Altı</small><b>{fullReport.demographics?.minors||0}</b></span><span><small>Yetişkin</small><b>{fullReport.demographics?.adults||0}</b></span><span><small>Veli Kaydı</small><b>{fullReport.demographics?.guardian_records||0}</b></span><span><small>Sağlık Notu</small><b>{fullReport.demographics?.medical_records||0}</b></span></div></section>
        </div>
        <section className="panel table-panel"><SectionTitle eyebrow="EĞİTMEN / ANTRENÖR" title="Çalışma Yükü"/><div className="table-wrap"><table><thead><tr><th>Ad Soyad</th><th>Unvan</th><th>Ders / Antrenman</th><th>Dönem / Takım</th></tr></thead><tbody>{(fullReport.instructors||[]).map((item)=><tr key={item.id}><td><b>{item.full_name}</b></td><td>{item.title||"—"}</td><td>{item.sessions}</td><td>{item.periods}</td></tr>)}{!(fullReport.instructors||[]).length&&<tr><td colSpan="4"><Empty text="Eğitmen / antrenör verisi yok."/></td></tr>}</tbody></table></div></section>
        <section className="panel table-panel"><SectionTitle eyebrow="ALACAK TAKİBİ" title="Geciken Taksitler"/><div className="table-wrap"><table><thead><tr><th>Öğrenci</th><th>Program</th><th>Vade</th><th>Taksit</th><th>Kalan</th></tr></thead><tbody>{(fullReport.overdue||[]).map((item)=><tr key={item.id}><td><b>{item.full_name}</b><small>{item.enrollment_no}</small></td><td>{item.course_title}</td><td>{dateText(item.due_date)}</td><td>{item.installment_no}</td><td className="orange-text"><b>{money(Number(item.amount||0)-Number(item.paid_amount||0))}</b></td></tr>)}{!(fullReport.overdue||[]).length&&<tr><td colSpan="5"><Empty text="Geciken taksit yok."/></td></tr>}</tbody></table></div></section>
      </>
    );
  }

  function renderContent() {
    if (loading) return <div className="loading-state"><div className="spinner" /><h3>Kurs yönetimi hazırlanıyor</h3><p>Başvuru, finans ve eğitim verileri yükleniyor.</p></div>;
    if (error) return <div className="error-state"><h3>Veriler alınamadı</h3><p>{error}</p><button type="button" className="button primary" onClick={() => loadAll()}>Tekrar Dene</button></div>;
    const views = {
      overview: renderOverview,
      courses: renderCourses,
      periods: renderPeriods,
      applications: renderApplications,
      students: renderStudents,
      finance: renderFinance,
      sessions: renderSessions,
      instructors: renderInstructors,
      reports: renderReports,
    };
    return (views[activeTab] || renderOverview)();
  }

  return (
    <div className="course-app">
      <div className="hotel-course-workspace">
        {renderHeader()}
        {renderSidebar()}
        <main className="course-main">{renderContent()}</main>
      </div>
      {notice && <div className="toast">{notice}</div>}
      {busy && <div className="busy-layer"><div className="spinner" /><span>İşlem tamamlanıyor...</span></div>}

      <Modal open={courseFormOpen} onClose={() => setCourseFormOpen(false)} title={courseEditingId ? "Kursu Düzenle" : "Yeni Kurs Oluştur"} wide>
        <form onSubmit={saveCourse} className="form-stack">
          <FormSection title="Temel Bilgiler" description="Flutter vitrini ve arama sonuçlarında görülecek bilgiler.">
            <div className="form-grid three">
              <Field label="Kurs Adı" required><input value={courseForm.title_tr} onChange={(e) => setCourseForm((o) => ({ ...o, title_tr: e.target.value }))} /></Field>
              <Field label="Kategori"><select value={courseForm.category_id} onChange={(e) => setCourseForm((o) => ({ ...o, category_id: e.target.value }))}><option value="">Kategori seçin</option>{categories.map((item) => <option key={item.id} value={item.id}>{item.name_tr}</option>)}</select></Field>
              <Field label="Yayın Durumu"><select value={courseForm.status} onChange={(e) => setCourseForm((o) => ({ ...o, status: e.target.value }))}><option value="draft">Taslak</option><option value="published">Yayınla</option><option value="paused">Duraklat</option><option value="completed">Tamamlandı</option></select></Field>
              <Field label="Program Türü"><select value={courseForm.program_type} onChange={(e) => setCourseForm((o) => ({ ...o, program_type: e.target.value }))}><option value="education">Genel Eğitim</option><option value="coaching">Yaşam Koçluğu</option><option value="sport">Spor Kursu / Kulüp</option><option value="language">Dil Eğitimi</option><option value="vocational">Mesleki Eğitim</option><option value="art">Sanat / Müzik</option><option value="workshop">Atölye / Seminer</option><option value="camp">Kamp</option><option value="private_lesson">Özel Ders</option><option value="membership">Üyelik Programı</option><option value="other">Diğer</option></select></Field>
              <Field label="Spor Dalı"><input value={courseForm.sport_branch || ""} onChange={(e) => setCourseForm((o) => ({ ...o, sport_branch: e.target.value }))} placeholder="Futbol, yüzme, karate..." /></Field>
              <Field label="Hedef Kitle"><input value={courseForm.target_audience || ""} onChange={(e) => setCourseForm((o) => ({ ...o, target_audience: e.target.value }))} placeholder="Çocuk, yetişkin, 2014 yaş grubu..." /></Field>
              <Field label="İngilizce Ad"><input value={courseForm.title_en || ""} onChange={(e) => setCourseForm((o) => ({ ...o, title_en: e.target.value }))} /></Field>
              <Field label="Almanca Ad"><input value={courseForm.title_de || ""} onChange={(e) => setCourseForm((o) => ({ ...o, title_de: e.target.value }))} /></Field>
              <Field label="Seviye"><input value={courseForm.level || ""} onChange={(e) => setCourseForm((o) => ({ ...o, level: e.target.value }))} placeholder="Başlangıç / İleri" /></Field>
            </div>
            <Field label="Kısa Tanıtım"><textarea rows="2" value={courseForm.short_description_tr || ""} onChange={(e) => setCourseForm((o) => ({ ...o, short_description_tr: e.target.value }))} /></Field>
            <Field label="Detaylı Açıklama"><textarea rows="5" value={courseForm.description_tr || ""} onChange={(e) => setCourseForm((o) => ({ ...o, description_tr: e.target.value }))} /></Field>
          </FormSection>

          <FormSection title="Eğitim Yapısı" description="Program, kazanım ve katılım şartlarını satır satır girin.">
            <div className="form-grid two">
              <Field label="Müfredat"><textarea rows="7" value={courseForm.curriculum_text || ""} onChange={(e) => setCourseForm((o) => ({ ...o, curriculum_text: e.target.value }))} placeholder="Her satıra bir ders konusu" /></Field>
              <Field label="Kazanımlar"><textarea rows="7" value={courseForm.outcomes_text || ""} onChange={(e) => setCourseForm((o) => ({ ...o, outcomes_text: e.target.value }))} placeholder="Kurs sonunda kazanılacak yetkinlikler" /></Field>
              <Field label="Katılım Şartları"><textarea rows="5" value={courseForm.requirements_text || ""} onChange={(e) => setCourseForm((o) => ({ ...o, requirements_text: e.target.value }))} /></Field>
              <Field label="Etiketler"><textarea rows="5" value={courseForm.tags_text || ""} onChange={(e) => setCourseForm((o) => ({ ...o, tags_text: e.target.value }))} placeholder="sertifika, meslek, online" /></Field>
            </div>
            <div className="form-grid four">
              <Field label="Eğitim Türü"><select value={courseForm.training_type} onChange={(e) => setCourseForm((o) => ({ ...o, training_type: e.target.value }))}><option value="face_to_face">Yüz Yüze</option><option value="online">Online</option><option value="hybrid">Hibrit</option></select></Field>
              <Field label="Toplam Saat"><input type="number" min="0" step="0.5" value={courseForm.total_hours || ""} onChange={(e) => setCourseForm((o) => ({ ...o, total_hours: e.target.value }))} /></Field>
              <Field label="Süre"><input value={courseForm.duration_text || ""} onChange={(e) => setCourseForm((o) => ({ ...o, duration_text: e.target.value }))} placeholder="8 hafta" /></Field>
              <Field label="Online Platform"><input value={courseForm.online_platform || ""} onChange={(e) => setCourseForm((o) => ({ ...o, online_platform: e.target.value }))} /></Field>
              <Field label="Minimum Yaş"><input type="number" value={courseForm.age_min || ""} onChange={(e) => setCourseForm((o) => ({ ...o, age_min: e.target.value }))} /></Field>
              <Field label="Maksimum Yaş"><input type="number" value={courseForm.age_max || ""} onChange={(e) => setCourseForm((o) => ({ ...o, age_max: e.target.value }))} /></Field>
              <Field label="Eğitim Dili"><input value={courseForm.language || "tr"} onChange={(e) => setCourseForm((o) => ({ ...o, language: e.target.value }))} /></Field>
              <Field label="Sertifika Başlığı"><input value={courseForm.certificate_title || ""} onChange={(e) => setCourseForm((o) => ({ ...o, certificate_title: e.target.value }))} /></Field>
              <Field label="Sezon"><input value={courseForm.season || ""} onChange={(e) => setCourseForm((o) => ({ ...o, season: e.target.value }))} placeholder="2026–2027" /></Field>
              <Field label="Üyelik Modeli"><input value={courseForm.membership_model || ""} onChange={(e) => setCourseForm((o) => ({ ...o, membership_model: e.target.value }))} placeholder="Aylık / dönemlik / tek sefer" /></Field>
              <Field label="Ekipman / Forma Notu"><input value={courseForm.equipment_notes || ""} onChange={(e) => setCourseForm((o) => ({ ...o, equipment_notes: e.target.value }))} /></Field>
              <Field label="Özel Başvuru Alanları"><input value={courseForm.custom_fields_text || ""} onChange={(e) => setCourseForm((o) => ({ ...o, custom_fields_text: e.target.value }))} placeholder="Her satıra bir alan" /></Field>
            </div>
          </FormSection>

      

<FormSection
  title="Görsel & İletişim"
  description="Kurs kapağı, galeri, adres ve iletişim bilgileri."
>
  <div className="form-grid two">
    <Field label="Kapak Görseli">
      <div className="course-upload-box">
        <ResimUpload
          label="Kapak Görseli Yükle"
          defaultPreview={
            courseForm.cover_image || ""
          }
          onUpload={(url) =>
            setCourseForm((previous) => ({
              ...previous,
              cover_image: url,
            }))
          }
        />

        {courseForm.cover_image && (
          <button
            type="button"
            className="image-remove-button"
            onClick={() =>
              setCourseForm((previous) => ({
                ...previous,
                cover_image: "",
              }))
            }
          >
            Kapak Görselini Kaldır
          </button>
        )}
      </div>
    </Field>

    <Field label="Galeri Görselleri">
      <div className="course-upload-box">
        <ResimUpload
          key={`course-gallery-upload-${
            listFromText(
              courseForm.gallery_text,
            ).length
          }`}
          label="Galeriye Görsel Ekle"
          defaultPreview=""
          onUpload={addGalleryImage}
        />

        {listFromText(
          courseForm.gallery_text,
        ).length > 0 && (
          <div className="course-gallery-preview">
            {listFromText(
              courseForm.gallery_text,
            ).map((url, index) => (
              <div
                className="course-gallery-item"
                key={`${url}-${index}`}
              >
                <img
                  src={url}
                  alt={`Kurs galerisi ${
                    index + 1
                  }`}
                />

                <button
                  type="button"
                  onClick={() =>
                    removeGalleryImage(url)
                  }
                  aria-label="Galeri görselini kaldır"
                  title="Görseli kaldır"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Field>

    <Field label="Tanıtım Videosu URL">
      <input
        value={
          courseForm.promo_video_url || ""
        }
        onChange={(e) =>
          setCourseForm((previous) => ({
            ...previous,
            promo_video_url:
              e.target.value,
          }))
        }
        placeholder="https://..."
      />
    </Field>

    <Field label="Adres">
      <textarea
        rows="4"
        value={courseForm.address || ""}
        onChange={(e) =>
          setCourseForm((previous) => ({
            ...previous,
            address: e.target.value,
          }))
        }
      />
    </Field>
  </div>

  <div className="form-grid four">
    <Field label="Şehir">
      <input
        value={courseForm.city || ""}
        onChange={(e) =>
          setCourseForm((previous) => ({
            ...previous,
            city: e.target.value,
          }))
        }
      />
    </Field>

    <Field label="İlçe">
      <input
        value={courseForm.district || ""}
        onChange={(e) =>
          setCourseForm((previous) => ({
            ...previous,
            district: e.target.value,
          }))
        }
      />
    </Field>

    <Field label="Telefon">
      <input
        value={
          courseForm.contact_phone || ""
        }
        onChange={(e) =>
          setCourseForm((previous) => ({
            ...previous,
            contact_phone:
              e.target.value,
          }))
        }
      />
    </Field>

    <Field label="WhatsApp">
      <input
        value={courseForm.whatsapp || ""}
        onChange={(e) =>
          setCourseForm((previous) => ({
            ...previous,
            whatsapp: e.target.value,
          }))
        }
      />
    </Field>

    <Field label="E-posta">
      <input
        type="email"
        value={
          courseForm.contact_email || ""
        }
        onChange={(e) =>
          setCourseForm((previous) => ({
            ...previous,
            contact_email:
              e.target.value,
          }))
        }
      />
    </Field>
  </div>
</FormSection>



          <FormSection title="Ücret & Yayın Ayarları" description="Onaylanan başvurunun account.js borçlandırması bu bilgilerden oluşur.">
            <div className="form-grid four">
              <Field label="Liste Fiyatı"><input type="number" min="0" step="0.01" value={courseForm.base_price || ""} onChange={(e) => setCourseForm((o) => ({ ...o, base_price: e.target.value }))} /></Field>
              <Field label="Satış Fiyatı"><input type="number" min="0" step="0.01" value={courseForm.sale_price || ""} onChange={(e) => setCourseForm((o) => ({ ...o, sale_price: e.target.value }))} /></Field>
              <Field label="Para Birimi"><select value={courseForm.currency} onChange={(e) => setCourseForm((o) => ({ ...o, currency: e.target.value }))}><option>TRY</option><option>EUR</option><option>CHF</option><option>USD</option></select></Field>
              <Field label="Taksit"><input type="number" min="1" max="36" value={courseForm.installment_count} onChange={(e) => setCourseForm((o) => ({ ...o, installment_count: e.target.value }))} /></Field>
            </div>
            <div className="switch-grid">
              <Switch label="Ücretsiz Kurs" checked={courseForm.free} onChange={(value) => setCourseForm((o) => ({ ...o, free: value }))} />
              <Switch label="Öne Çıkan" checked={courseForm.featured} onChange={(value) => setCourseForm((o) => ({ ...o, featured: value }))} />
              <Switch label="Başvurular Açık" checked={courseForm.applications_open} onChange={(value) => setCourseForm((o) => ({ ...o, applications_open: value }))} />
              <Switch label="Veli Bilgisi Zorunlu" checked={courseForm.guardian_required} onChange={(value) => setCourseForm((o) => ({ ...o, guardian_required: value }))} />
              <Switch label="Sağlık Bilgisi Zorunlu" checked={courseForm.health_form_required} onChange={(value) => setCourseForm((o) => ({ ...o, health_form_required: value }))} />
              <Switch label="Yoklama Aktif" checked={courseForm.attendance_enabled} onChange={(value) => setCourseForm((o) => ({ ...o, attendance_enabled: value }))} />
              <Switch label="Performans Takibi" checked={courseForm.assessment_enabled} onChange={(value) => setCourseForm((o) => ({ ...o, assessment_enabled: value }))} />
              <Switch label="Sertifika Verilecek" checked={courseForm.certificate_enabled} onChange={(value) => setCourseForm((o) => ({ ...o, certificate_enabled: value }))} />
            </div>
          </FormSection>
          <ModalActions onCancel={() => setCourseFormOpen(false)} submitLabel={courseEditingId ? "Kursu Güncelle" : "Kursu Kaydet"} busy={busy} />
        </form>
      </Modal>

      <Modal open={periodFormOpen} onClose={() => setPeriodFormOpen(false)} title={periodEditingId ? "Dönemi Düzenle" : "Yeni Kurs Dönemi"}>
        <form onSubmit={savePeriod} className="form-stack">
          <div className="form-grid two">
            <Field label="Kurs" required><select value={periodForm.course_id} disabled={Boolean(periodEditingId)} onChange={(e) => setPeriodForm((o) => ({ ...o, course_id: e.target.value }))}><option value="">Kurs seçin</option>{courses.filter((item) => item.status !== "archived").map((item) => <option key={item.id} value={item.id}>{item.title_tr}</option>)}</select></Field>
            <Field label="Dönem Adı" required><input value={periodForm.title} onChange={(e) => setPeriodForm((o) => ({ ...o, title: e.target.value }))} placeholder="2026 Eylül Hafta Sonu" /></Field>
            <Field label="Grup / Takım Kodu"><input value={periodForm.group_code || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, group_code: e.target.value }))} /></Field>
            <Field label="Takım / Grup Adı"><input value={periodForm.team_name || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, team_name: e.target.value }))} /></Field>
            <Field label="Yaş Grubu"><input value={periodForm.age_group || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, age_group: e.target.value }))} placeholder="2014 doğumlular / 8–10 yaş" /></Field>
            <Field label="Seviye"><input value={periodForm.sport_level || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, sport_level: e.target.value }))} /></Field>
            <Field label="Başlangıç"><input type="date" value={periodForm.start_date || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, start_date: e.target.value }))} /></Field>
            <Field label="Bitiş"><input type="date" value={periodForm.end_date || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, end_date: e.target.value }))} /></Field>
            <Field label="Son Başvuru"><input type="date" value={periodForm.application_deadline || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, application_deadline: e.target.value }))} /></Field>
            <Field label="Günler"><input value={periodForm.days_text || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, days_text: e.target.value }))} /></Field>
            <Field label="Başlangıç Saati"><input type="time" value={periodForm.start_time || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, start_time: e.target.value }))} /></Field>
            <Field label="Bitiş Saati"><input type="time" value={periodForm.end_time || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, end_time: e.target.value }))} /></Field>
            <Field label="Kontenjan"><input type="number" min="0" value={periodForm.capacity} onChange={(e) => setPeriodForm((o) => ({ ...o, capacity: e.target.value }))} /></Field>
            <Field label="Yedek Kontenjan"><input type="number" min="0" value={periodForm.waitlist_capacity} onChange={(e) => setPeriodForm((o) => ({ ...o, waitlist_capacity: e.target.value }))} /></Field>
            <Field label="Dönem Ücreti"><input type="number" min="0" step="0.01" value={periodForm.price || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, price: e.target.value }))} /></Field>
            <Field label="Ücret Periyodu"><select value={periodForm.billing_cycle || "one_time"} onChange={(e) => setPeriodForm((o) => ({ ...o, billing_cycle: e.target.value }))}><option value="one_time">Tek Sefer</option><option value="monthly">Aylık Aidat</option><option value="weekly">Haftalık</option><option value="per_session">Ders / Antrenman Başına</option><option value="term">Dönemlik</option></select></Field>
            <Field label="Tekrarlayan Ay"><input type="number" min="1" value={periodForm.recurring_months || 1} onChange={(e) => setPeriodForm((o) => ({ ...o, recurring_months: e.target.value }))} /></Field>
            <Field label="Otomatik Ders / Antrenman Başlığı"><input value={periodForm.auto_session_title || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, auto_session_title: e.target.value }))} /></Field>
            <Field label="Taksit"><input type="number" min="1" value={periodForm.installment_count} onChange={(e) => setPeriodForm((o) => ({ ...o, installment_count: e.target.value }))} /></Field>
            <Field label="Sınıf"><input value={periodForm.classroom || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, classroom: e.target.value }))} /></Field>
            <Field label="Online Bağlantı"><input value={periodForm.meeting_url || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, meeting_url: e.target.value }))} /></Field>
            <Field label="Durum"><select value={periodForm.status} onChange={(e) => setPeriodForm((o) => ({ ...o, status: e.target.value }))}><option value="planned">Planlandı</option><option value="open">Başvuru Açık</option><option value="full">Kontenjan Dolu</option><option value="started">Başladı</option><option value="completed">Tamamlandı</option><option value="cancelled">İptal</option></select></Field>
          </div>
          <Field label="Not"><textarea rows="3" value={periodForm.notes || ""} onChange={(e) => setPeriodForm((o) => ({ ...o, notes: e.target.value }))} /></Field>
          <ModalActions onCancel={() => setPeriodFormOpen(false)} submitLabel={periodEditingId ? "Dönemi Güncelle" : "Dönem Oluştur"} busy={busy} />
        </form>
      </Modal>

      <Modal open={applicationActionOpen} onClose={() => setApplicationActionOpen(false)} title="Başvuru İşlemi">
        {selectedApplication && <form onSubmit={saveApplicationAction} className="form-stack">
          <div className="application-summary"><Avatar name={selectedApplication.full_name} large /><div><h3>{selectedApplication.full_name}</h3><p>{selectedApplication.course_title}</p><small>{selectedApplication.application_no} · {selectedApplication.phone || "Telefon yok"}</small></div><Status status={selectedApplication.status} /></div>
          <div className="detail-note"><b>Başvuru Notu</b><p>{selectedApplication.application_note || "Not bırakılmadı."}</p></div>
          {(selectedApplication.guardian_name || selectedApplication.medical_notes || selectedApplication.license_no) && <div className="detail-note"><b>Sporcu / Veli Bilgileri</b><p>Veli: {selectedApplication.guardian_name || "—"} · {selectedApplication.guardian_phone || "—"}<br/>Sağlık: {selectedApplication.medical_notes || "—"}<br/>Lisans: {selectedApplication.license_no || "—"} · Forma: {selectedApplication.jersey_size || "—"}</p></div>}
          <Field label="Yeni Durum"><select value={applicationAction.status} onChange={(e) => setApplicationAction((o) => ({ ...o, status: e.target.value }))}><option value="reviewing">İnceleniyor</option><option value="document_pending">Belge Bekleniyor</option><option value="pre_approved">Ön Onay</option><option value="payment_pending">Ödeme Bekleniyor</option><option value="approved">Onayla ve Kursiyer Oluştur</option><option value="rejected">Reddet</option><option value="cancelled">İptal</option></select></Field>
          {applicationAction.status === "approved" && <div className="approval-finance"><h4>Finans Kaydı</h4><p>Onay sırasında account.js hesabı, borç hareketi ve taksit planı otomatik oluşturulur.</p><div className="form-grid two"><Field label="Anlaşılan Ücret"><input type="number" min="0" step="0.01" value={applicationAction.agreed_price} onChange={(e) => setApplicationAction((o) => ({ ...o, agreed_price: e.target.value }))} /></Field><Field label="Taksit"><input type="number" min="1" value={applicationAction.installment_count} onChange={(e) => setApplicationAction((o) => ({ ...o, installment_count: e.target.value }))} /></Field><Field label="İndirim"><input type="number" min="0" step="0.01" value={applicationAction.discount_amount} onChange={(e) => setApplicationAction((o) => ({ ...o, discount_amount: e.target.value }))} /></Field><Field label="Burs"><input type="number" min="0" step="0.01" value={applicationAction.scholarship_amount} onChange={(e) => setApplicationAction((o) => ({ ...o, scholarship_amount: e.target.value }))} /></Field></div></div>}
          {applicationAction.status === "rejected" && <Field label="Ret Nedeni" required><textarea rows="3" value={applicationAction.rejection_reason} onChange={(e) => setApplicationAction((o) => ({ ...o, rejection_reason: e.target.value }))} /></Field>}
          <Field label="Yönetici Notu"><textarea rows="3" value={applicationAction.admin_note} onChange={(e) => setApplicationAction((o) => ({ ...o, admin_note: e.target.value }))} /></Field>
          <Field label="Kullanıcıya Mesaj"><textarea rows="3" value={applicationAction.user_message} onChange={(e) => setApplicationAction((o) => ({ ...o, user_message: e.target.value }))} placeholder="Flutter bildiriminde gösterilecek mesaj" /></Field>
          <ModalActions onCancel={() => setApplicationActionOpen(false)} submitLabel="İşlemi Tamamla" busy={busy} />
        </form>}
      </Modal>


<Modal
  open={manualEnrollmentOpen}
  onClose={() =>
    setManualEnrollmentOpen(false)
  }
  title="Manuel Kursiyer / Sporcu Kaydı"
  wide
>
  <form
    onSubmit={saveManualEnrollment}
    className="form-stack"
  >
    <FormSection
      title="Program ve Sınıf / Takım"
      description="Kişi seçilen grubun yoklama listesine otomatik eklenir."
    >
      <div className="form-grid two">
        <Field
          label="Program"
          required
        >
          <select
            required
            value={
              manualEnrollmentForm.course_id
            }
            onChange={(e) => {
              const courseId =
                e.target.value;

              const course =
                courses.find(
                  (item) =>
                    String(item.id) ===
                    String(courseId),
                );

              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  course_id: courseId,
                  period_id: "",
                  participant_type:
                    course?.program_type ===
                    "sport"
                      ? "minor_or_sport"
                      : "adult",
                  agreed_price:
                    course?.sale_price ||
                    course?.base_price ||
                    "",
                  installment_count:
                    course?.installment_count ||
                    1,
                }),
              );
            }}
          >
            <option value="">
              Program seçin
            </option>

            {courses.map((item) => (
              <option
                key={item.id}
                value={item.id}
              >
                {item.title_tr}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Sınıf / Grup / Takım"
          required
        >
          <select
            required
            value={
              manualEnrollmentForm.period_id
            }
            onChange={(e) => {
              const periodId =
                e.target.value;

              const period =
                periods.find(
                  (item) =>
                    String(item.id) ===
                    String(periodId),
                );

              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  period_id: periodId,
                  agreed_price:
                    period?.price ||
                    old.agreed_price,
                  installment_count:
                    period?.installment_count ||
                    old.installment_count,
                }),
              );
            }}
          >
            <option value="">
              Sınıf veya takım seçin
            </option>

            {periods
              .filter(
                (item) =>
                  String(item.course_id) ===
                  String(
                    manualEnrollmentForm
                      .course_id,
                  ),
              )
              .map((item) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.title}
                  {item.team_name
                    ? ` · ${item.team_name}`
                    : ""}
                  {item.age_group
                    ? ` · ${item.age_group}`
                    : ""}
                </option>
              ))}
          </select>
        </Field>
      </div>
    </FormSection>

    <FormSection
      title="Kursiyer / Sporcu Bilgileri"
      description="Dershane öğrencisi veya kulüp sporcusu bilgileri."
    >
      <div className="form-grid three">
        <Field
          label="Ad Soyad"
          required
        >
          <input
            required
            value={
              manualEnrollmentForm.full_name
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  full_name:
                    e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="Telefon">
          <input
            value={
              manualEnrollmentForm.phone
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  phone: e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="E-posta">
          <input
            type="email"
            value={
              manualEnrollmentForm.email
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  email: e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="Doğum Tarihi">
          <input
            type="date"
            value={
              manualEnrollmentForm.birth_date
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  birth_date:
                    e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="Veli Ad Soyad">
          <input
            value={
              manualEnrollmentForm
                .guardian_name
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  guardian_name:
                    e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="Veli Telefonu">
          <input
            value={
              manualEnrollmentForm
                .guardian_phone
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  guardian_phone:
                    e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="Acil İletişim">
          <input
            value={
              manualEnrollmentForm
                .emergency_contact_phone
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  emergency_contact_phone:
                    e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="Lisans No">
          <input
            value={
              manualEnrollmentForm.license_no
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  license_no:
                    e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="Forma Bedeni">
          <input
            value={
              manualEnrollmentForm.jersey_size
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  jersey_size:
                    e.target.value,
                }),
              )
            }
          />
        </Field>
      </div>

      <Field label="Sağlık / Sakatlık Notu">
        <textarea
          rows="3"
          value={
            manualEnrollmentForm.medical_notes
          }
          onChange={(e) =>
            setManualEnrollmentForm(
              (old) => ({
                ...old,
                medical_notes:
                  e.target.value,
              }),
            )
          }
        />
      </Field>
    </FormSection>

    <FormSection
      title="Ücret Bilgileri"
      description="Finans hesabı ve taksit planı otomatik oluşturulur."
    >
      <div className="form-grid four">
        <Field label="Kayıt Ücreti">
          <input
            type="number"
            min="0"
            step="0.01"
            value={
              manualEnrollmentForm
                .agreed_price
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  agreed_price:
                    e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="İndirim">
          <input
            type="number"
            min="0"
            step="0.01"
            value={
              manualEnrollmentForm
                .discount_amount
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  discount_amount:
                    e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="Burs">
          <input
            type="number"
            min="0"
            step="0.01"
            value={
              manualEnrollmentForm
                .scholarship_amount
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  scholarship_amount:
                    e.target.value,
                }),
              )
            }
          />
        </Field>

        <Field label="Taksit Sayısı">
          <input
            type="number"
            min="1"
            value={
              manualEnrollmentForm
                .installment_count
            }
            onChange={(e) =>
              setManualEnrollmentForm(
                (old) => ({
                  ...old,
                  installment_count:
                    e.target.value,
                }),
              )
            }
          />
        </Field>
      </div>

      <Field label="Kayıt Notu">
        <textarea
          rows="3"
          value={
            manualEnrollmentForm.notes
          }
          onChange={(e) =>
            setManualEnrollmentForm(
              (old) => ({
                ...old,
                notes: e.target.value,
              }),
            )
          }
        />
      </Field>
    </FormSection>

    <ModalActions
      onCancel={() =>
        setManualEnrollmentOpen(false)
      }
      submitLabel="Kursiyeri / Sporcuyu Kaydet"
      busy={busy}
    />
  </form>
</Modal>


      <Modal open={paymentOpen} onClose={() => setPaymentOpen(false)} title="Kurs / Program Tahsilatı">
        {selectedEnrollment && <form onSubmit={savePayment} className="form-stack">
          <div className="finance-summary-card"><div><span>Kursiyer</span><b>{selectedEnrollment.full_name}</b><small>{selectedEnrollment.course_title}</small></div><div><span>Kalan Bakiye</span><strong>{money(selectedEnrollment.remaining_amount, selectedEnrollment.currency)}</strong></div></div>
          <div className="form-grid two"><Field label="Tahsilat Tutarı" required><input type="number" min="0.01" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm((o) => ({ ...o, amount: e.target.value }))} /></Field><Field label="Ödeme Yöntemi"><select value={paymentForm.payment_method} onChange={(e) => setPaymentForm((o) => ({ ...o, payment_method: e.target.value }))}><option value="cash">Nakit</option><option value="card">Kart</option><option value="bank">Banka / Havale</option><option value="online">Online</option><option value="other">Diğer</option></select></Field><Field label="Ödeme Tarihi"><input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm((o) => ({ ...o, payment_date: e.target.value }))} /></Field><Field label="Referans No"><input value={paymentForm.reference_no} onChange={(e) => setPaymentForm((o) => ({ ...o, reference_no: e.target.value }))} /></Field></div>
          <Field label="Açıklama"><textarea rows="3" value={paymentForm.note} onChange={(e) => setPaymentForm((o) => ({ ...o, note: e.target.value }))} /></Field>
          <div className="integration-note"><b>Account.js + Kasa birlikte</b><span>Tahsilat öğrencinin borcunu düşürür, mevcut cash_transactions tablosuna Kurs / Program geliri olarak tekil kayıt atar ve Flutter’da anında görünür.</span></div>
          <ModalActions onCancel={() => setPaymentOpen(false)} submitLabel="Tahsilatı Kaydet" busy={busy} />
        </form>}
      </Modal>

      <Modal open={certificateOpen} onClose={() => setCertificateOpen(false)} title="Sertifika Oluştur">
        <form onSubmit={saveCertificate} className="form-stack">
          <Field label="Sertifika Başlığı"><input value={certificateForm.title} onChange={(e) => setCertificateForm((o) => ({ ...o, title: e.target.value }))} /></Field>
          <Field label="Düzenlenme Tarihi"><input type="date" value={certificateForm.issue_date} onChange={(e) => setCertificateForm((o) => ({ ...o, issue_date: e.target.value }))} /></Field>
          <Field label="Hazır PDF / Görsel URL"><input value={certificateForm.file_url} onChange={(e) => setCertificateForm((o) => ({ ...o, file_url: e.target.value }))} /></Field>
          <div className="integration-note"><b>Doğrulama kodu otomatik</b><span>Sertifika için benzersiz numara ve herkese açık doğrulama kodu üretilecek.</span></div>
          <ModalActions onCancel={() => setCertificateOpen(false)} submitLabel="Sertifikayı Oluştur" busy={busy} />
        </form>
      </Modal>

      <Modal open={studentDetailOpen} onClose={() => setStudentDetailOpen(false)} title="Katılımcı / Sporcu Dosyası" wide>
        {!studentDetail ? <div className="modal-loading"><div className="spinner" />Detay yükleniyor...</div> : <div className="student-detail-layout">
          <section className="student-profile"><Avatar name={studentDetail.full_name} large /><h3>{studentDetail.full_name}</h3><p>{studentDetail.course_title}</p><Status status={studentDetail.payment_status} /><div className="profile-info"><span><b>Kayıt</b>{studentDetail.enrollment_no}</span><span><b>Telefon</b>{studentDetail.phone || "—"}</span><span><b>E-posta</b>{studentDetail.email || "—"}</span><span><b>Dönem / Grup</b>{studentDetail.period_title || "—"}</span><span><b>Veli</b>{studentDetail.guardian_name || "—"}</span><span><b>Acil İletişim</b>{studentDetail.emergency_contact_phone || "—"}</span><span><b>Lisans</b>{studentDetail.license_no || "—"}</span></div></section>
          <section className="student-detail-main"><div className="student-finance big"><span><small>Toplam</small><b>{money(studentDetail.agreed_price, studentDetail.currency)}</b></span><span><small>Ödenen</small><b className="green-text">{money(studentDetail.actual_paid, studentDetail.currency)}</b></span><span><small>Kalan</small><b className="orange-text">{money(studentDetail.remaining_amount, studentDetail.currency)}</b></span></div><h4>Taksit Planı</h4><div className="installment-list">{(studentDetail.installments || []).map((item) => <article key={item.id}><span>{item.installment_no}. Taksit<small>{dateText(item.due_date)}</small></span><b>{money(item.amount, studentDetail.currency)}</b><Status status={item.status} /></article>)}</div><h4>Ödeme Geçmişi</h4><div className="payment-list">{(studentDetail.payments || []).map((item) => <article key={item.id}><span><b>{paymentLabel(item.payment_method)}</b><small>{dateTimeText(item.payment_date)}</small></span><strong>{money(item.amount, item.currency)}</strong></article>)}{!(studentDetail.payments || []).length && <Empty text="Henüz ödeme yok." />}</div><h4>Yoklama & Performans Özeti</h4><div className="payment-list">{(studentDetail.attendance || []).slice(0,12).map((item)=><article key={item.id}><span><b>{item.session_title}</b><small>{dateText(item.session_date)} · {STATUS_LABELS[item.status]||item.status}</small></span><strong>{item.performance_score ? `${item.performance_score}/100` : "—"}</strong></article>)}{!(studentDetail.attendance||[]).length&&<Empty text="Yoklama kaydı yok."/>}</div><h4>Gelişim Notları</h4><div className="payment-list">{(studentDetail.assessments || []).map((item) => (
  <article key={item.id}>
    <span>
      <b>{item.title}</b>
      <small>{item.note || item.grade || ""}</small>
    </span>

    <strong>
      {item.score !== null && item.score !== undefined
        ? `${item.score}/${item.max_score || 100}`
        : item.grade || "—"}
    </strong>

    <button
      type="button"
      className="danger"
      onClick={() => deleteAssessmentPermanently(item)}
    >
      Sil
    </button>
  </article>
))}{!(studentDetail.assessments||[]).length&&<Empty text="Gelişim değerlendirmesi yok."/>}</div><h4>Yeni Değerlendirme</h4><form className="form-stack assessment-form" onSubmit={saveAssessment}><div className="form-grid two"><Field label="Başlık" required><input value={assessmentForm.title} onChange={(e)=>setAssessmentForm((o)=>({...o,title:e.target.value}))} placeholder="Aylık gelişim / teknik değerlendirme"/></Field><Field label="Tür"><select value={assessmentForm.assessment_type} onChange={(e)=>setAssessmentForm((o)=>({...o,assessment_type:e.target.value}))}><option value="progress">Gelişim</option><option value="performance">Performans</option><option value="exam">Sınav</option><option value="coach_note">Antrenör Notu</option><option value="behavior">Davranış / Disiplin</option></select></Field><Field label="Puan"><input type="number" min="0" value={assessmentForm.score} onChange={(e)=>setAssessmentForm((o)=>({...o,score:e.target.value}))}/></Field><Field label="Maksimum"><input type="number" min="1" value={assessmentForm.max_score} onChange={(e)=>setAssessmentForm((o)=>({...o,max_score:e.target.value}))}/></Field><Field label="Derece / Seviye"><input value={assessmentForm.grade} onChange={(e)=>setAssessmentForm((o)=>({...o,grade:e.target.value}))} placeholder="A, İyi, Gelişiyor..."/></Field><Field label="Öğrenci Görsün"><select value={assessmentForm.visible_to_student ? "yes" : "no"} onChange={(e)=>setAssessmentForm((o)=>({...o,visible_to_student:e.target.value==="yes"}))}><option value="yes">Evet</option><option value="no">Hayır</option></select></Field></div><Field label="Değerlendirme Notu"><textarea rows="3" value={assessmentForm.note} onChange={(e)=>setAssessmentForm((o)=>({...o,note:e.target.value}))}/></Field><button type="submit" className="button primary" disabled={busy}>Değerlendirmeyi Kaydet</button></form></section>
        </div>}
      </Modal>

      <Modal
        open={instructorFormOpen}
        onClose={() => {
          setInstructorFormOpen(false);
          setInstructorEditingId(null);
          setInstructorForm(EMPTY_INSTRUCTOR);
        }}
        title={
          instructorEditingId
            ? "Eğitmen / Antrenör Düzenle"
            : "Yeni Eğitmen / Antrenör"
        }
      >
        <form onSubmit={saveInstructor} className="form-stack"><div className="form-grid two"><Field label="Ad Soyad" required><input value={instructorForm.full_name} onChange={(e) => setInstructorForm((o) => ({ ...o, full_name: e.target.value }))} /></Field><Field label="Unvan"><input value={instructorForm.title} onChange={(e) => setInstructorForm((o) => ({ ...o, title: e.target.value }))} /></Field><Field label="Telefon"><input value={instructorForm.phone} onChange={(e) => setInstructorForm((o) => ({ ...o, phone: e.target.value }))} /></Field><Field label="E-posta"><input value={instructorForm.email} onChange={(e) => setInstructorForm((o) => ({ ...o, email: e.target.value }))} /></Field><Field label="Fotoğraf URL"><input value={instructorForm.photo_url} onChange={(e) => setInstructorForm((o) => ({ ...o, photo_url: e.target.value }))} /></Field><Field label="Uzmanlıklar"><input value={instructorForm.specialties_text} onChange={(e) => setInstructorForm((o) => ({ ...o, specialties_text: e.target.value }))} /></Field></div><Field label="Öz Geçmiş"><textarea rows="5" value={instructorForm.biography} onChange={(e) => setInstructorForm((o) => ({ ...o, biography: e.target.value }))} /></Field><ModalActions
          onCancel={() => {
            setInstructorFormOpen(false);
            setInstructorEditingId(null);
            setInstructorForm(EMPTY_INSTRUCTOR);
          }}
          submitLabel={
            instructorEditingId
              ? "Değişiklikleri Kaydet"
              : "Eğitmeni Kaydet"
          }
          busy={busy}
        /></form>
      </Modal>

      <Modal
        open={sessionFormOpen}
        onClose={() => {
          setSessionFormOpen(false);
          setSessionEditingId(null);
          setSessionForm(EMPTY_SESSION);
        }}
        title={
          sessionEditingId
            ? "Ders / Antrenman Düzenle"
            : "Yeni Ders / Antrenman"
        }
      >
        <form onSubmit={saveSession} className="form-stack"><div className="form-grid two"><Field label="Kurs" required><select value={sessionForm.course_id} onChange={(e) => setSessionForm((o) => ({ ...o, course_id: e.target.value, period_id: "" }))}><option value="">Kurs seçin</option>{courses.map((item) => <option key={item.id} value={item.id}>{item.title_tr}</option>)}</select></Field>


<Field
  label="Sınıf / Grup / Takım"
  required
>
  <select
    required={
      sessionForm.attendance_required !==
      false
    }
    value={sessionForm.period_id}
    onChange={(e) =>
      setSessionForm((old) => ({
        ...old,
        period_id: e.target.value,
      }))
    }
  >
    <option value="">
      Sınıf veya takım seçin
    </option>

    {periods
      .filter(
        (item) =>
          String(item.course_id) ===
          String(sessionForm.course_id),
      )
      .map((item) => (
        <option
          key={item.id}
          value={item.id}
        >
          {item.title}
          {item.team_name
            ? ` · ${item.team_name}`
            : ""}
          {item.age_group
            ? ` · ${item.age_group}`
            : ""}
        </option>
      ))}
  </select>
</Field>


<Field label="Ders Başlığı" required><input value={sessionForm.title} onChange={(e) => setSessionForm((o) => ({ ...o, title: e.target.value }))} /></Field><Field label="Tarih" required><input type="date" value={sessionForm.session_date} onChange={(e) => setSessionForm((o) => ({ ...o, session_date: e.target.value }))} /></Field><Field label="Başlangıç"><input type="time" value={sessionForm.start_time} onChange={(e) => setSessionForm((o) => ({ ...o, start_time: e.target.value }))} /></Field><Field label="Bitiş"><input type="time" value={sessionForm.end_time} onChange={(e) => setSessionForm((o) => ({ ...o, end_time: e.target.value }))} /></Field><Field label="Eğitmen"><select value={sessionForm.instructor_id} onChange={(e) => setSessionForm((o) => ({ ...o, instructor_id: e.target.value }))}><option value="">Eğitmen seçin</option>{instructors.map((item) => <option key={item.id} value={item.id}>{item.full_name}</option>)}</select></Field><Field label="Sınıf"><input value={sessionForm.classroom} onChange={(e) => setSessionForm((o) => ({ ...o, classroom: e.target.value }))} /></Field><Field label="Program Türü"><select value={sessionForm.session_type || "lesson"} onChange={(e) => setSessionForm((o) => ({ ...o, session_type: e.target.value }))}><option value="lesson">Ders</option><option value="training">Antrenman</option><option value="match">Maç / Müsabaka</option><option value="workshop">Atölye</option><option value="exam">Sınav</option><option value="meeting">Veli / Grup Toplantısı</option><option value="camp">Kamp</option><option value="other">Diğer</option></select></Field><Field label="Konum"><input value={sessionForm.location_text || ""} onChange={(e) => setSessionForm((o) => ({ ...o, location_text: e.target.value }))} /></Field><Field label="Durum"><select value={sessionForm.status} onChange={(e) => setSessionForm((o) => ({ ...o, status: e.target.value }))}><option value="planned">Planlandı</option><option value="started">Başladı</option><option value="completed">Tamamlandı</option><option value="cancelled">İptal</option></select></Field></div><Field label="Ders Açıklaması"><textarea rows="3" value={sessionForm.description} onChange={(e) => setSessionForm((o) => ({ ...o, description: e.target.value }))} /></Field><ModalActions
          onCancel={() => {
            setSessionFormOpen(false);
            setSessionEditingId(null);
            setSessionForm(EMPTY_SESSION);
          }}
          submitLabel={
            sessionEditingId
              ? "Değişiklikleri Kaydet"
              : "Dersi Kaydet"
          }
          busy={busy}
        /></form>
      </Modal>

      <Modal open={attendanceOpen} onClose={() => setAttendanceOpen(false)} title={attendanceSession ? `${attendanceSession.title} Yoklaması` : "Yoklama"} wide>
        <div className="attendance-list">{attendanceRows.map((row, index) => <article key={row.enrollment_id}><div className="person-cell"><Avatar name={row.full_name} /><span><b>{row.full_name}</b><small>{row.enrollment_no}</small></span></div>


<select
  value={row.status}
  onChange={(e) =>
    setAttendanceRows((old) =>
      old.map((item, i) =>
        i === index
          ? {
              ...item,
              status: e.target.value,
            }
          : item,
      ),
    )
  }
>
  <option value="unmarked">
    İşaretlenmedi
  </option>

  <option value="present">
    Katıldı
  </option>



<option value="absent">Katılmadı</option><option value="late">Geç Geldi</option><option value="excused">Mazeretli</option><option value="medical">Raporlu</option><option value="trial">Deneme Öğrencisi</option><option value="online">Online Katıldı</option></select><input type="number" min="0" max="100" value={row.performance_score || ""} onChange={(e) => setAttendanceRows((old) => old.map((item, i) => i === index ? { ...item, performance_score: e.target.value } : item))} placeholder="Performans 0–100" /><input value={row.coach_note || ""} onChange={(e) => setAttendanceRows((old) => old.map((item, i) => i === index ? { ...item, coach_note: e.target.value } : item))} placeholder="Eğitmen / antrenör notu" /></article>)}{!attendanceRows.length && <Empty text="Bu dönemde aktif öğrenci / sporcu bulunamadı." />}</div><div className="modal-actions"><button type="button" className="button ghost" onClick={() => setAttendanceOpen(false)}>Vazgeç</button><button type="button" className="button primary" onClick={saveAttendance} disabled={busy}>Yoklamayı Kaydet</button></div>
      </Modal>

      <style>{COURSE_STYLES}</style>
    </div>
  );
}

function programTypeLabel(value) {
  return {
    education: "Genel Eğitim", sport: "Spor Kursu / Kulüp", coaching: "Yaşam Koçluğu",
    language: "Dil Eğitimi", vocational: "Mesleki Eğitim", art: "Sanat / Müzik",
    workshop: "Atölye / Seminer", camp: "Kamp", private_lesson: "Özel Ders",
    membership: "Üyelik Programı", other: "Diğer",
  }[value] || "Genel Eğitim";
}

function sessionTypeLabel(value) {
  return { lesson: "Ders", training: "Antrenman", match: "Maç", workshop: "Atölye", exam: "Sınav", meeting: "Toplantı", camp: "Kamp", other: "Diğer" }[value] || "Ders";
}

function paymentLabel(value) {
  return { cash: "Nakit", card: "Kart", bank: "Banka / Havale", online: "Online", other: "Diğer" }[value] || value || "Belirtilmedi";
}

function Metric({ label, value, detail, tone }) {
  const icons = {
    navy: "📚",
    orange: "🗓️",
    purple: "📥",
    green: "✅",
    blue: "👥",
    red: "⚠️",
  };
  return (
    <article className={`metric-card ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
      <i />
      <em className="metric-icon" aria-hidden="true">{icons[tone] || "📊"}</em>
    </article>
  );
}

function Status({ status, label }) {
  return <span className={`status ${statusTone(status)}`}><i />{label || STATUS_LABELS[status] || status || "—"}</span>;
}

function Avatar({ name, large = false }) {
  const parts = String(name || "K").trim().split(/\s+/).filter(Boolean);
  const initials = (parts[0]?.[0] || "K") + (parts.length > 1 ? parts[parts.length - 1][0] : "");
  return <span className={`avatar ${large ? "large" : ""}`}>{initials.toLocaleUpperCase("tr-TR")}</span>;
}

function SectionTitle({ eyebrow, title, action }) {
  return <div className="section-title"><div><span>{eyebrow}</span><h3>{title}</h3></div>{action}</div>;
}

function PageTitle({ eyebrow, title, description, action }) {
  return <div className="page-title"><div><span>{eyebrow}</span><h2>{title}</h2><p>{description}</p></div>{action && <div>{action}</div>}</div>;
}

function Empty({ text }) {
  return <div className="empty"><span>—</span><p>{text}</p></div>;
}

function Field({ label, required = false, children }) {
  return <label className="field"><span>{label}{required && <b>*</b>}</span>{children}</label>;
}

function Switch({ label, checked, onChange }) {
  return <button className={`switch ${checked ? "on" : ""}`} type="button" onClick={() => onChange(!checked)}><i><em /></i><span>{label}</span></button>;
}

function FormSection({ title, description, children }) {
  return <section className="form-section"><div className="form-section-title"><h3>{title}</h3><p>{description}</p></div><div className="form-section-body">{children}</div></section>;
}

function ModalActions({ onCancel, submitLabel, busy }) {
  return <div className="modal-actions"><button type="button" className="button ghost" onClick={onCancel}>Vazgeç</button><button type="submit" className="button primary" disabled={busy}>{busy ? "Kaydediliyor..." : submitLabel}</button></div>;
}

function Modal({ open, onClose, title, children, wide = false }) {
  if (!open) return null;
  return <div className="modal-layer" role="dialog" aria-modal="true"><button type="button" className="modal-backdrop" onClick={onClose} aria-label="Kapat" /><section className={`modal ${wide ? "wide" : ""}`}><header><div><span>MESYAT KURS & PROGRAM YÖNETİMİ</span><h2>{title}</h2></div><button type="button" onClick={onClose}>×</button></header><div className="modal-body">{children}</div></section></div>;
}

const COURSE_STYLES = `
:root{--navy:#082f49;--navy2:#0b3b5b;--blue:#0e7490;--orange:#f47b20;--green:#14866d;--red:#c2413b;--ink:#152936;--muted:#6e7f8a;--line:#dce5ea;--bg:#f2f6f8;--white:#fff;--shadow:0 16px 50px rgba(8,47,73,.09)}
*{box-sizing:border-box}.course-app{min-height:100vh;background:var(--bg);color:var(--ink);font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.course-app button,.course-app input,.course-app select,.course-app textarea{font:inherit}.course-header{position:sticky;top:0;z-index:40;display:flex;align-items:center;justify-content:space-between;gap:24px;padding:18px 28px;background:rgba(255,255,255,.96);border-bottom:1px solid var(--line);backdrop-filter:blur(14px)}.header-left{display:flex;align-items:center;gap:15px}.brand-mark{width:47px;height:47px;display:grid;place-items:center;border-radius:15px;background:linear-gradient(145deg,var(--navy),var(--blue));color:white;font-size:22px;font-weight:900;box-shadow:0 9px 22px rgba(8,47,73,.2)}.eyebrow{display:block;color:var(--orange);font-size:10px;font-weight:900;letter-spacing:.16em}.header-left h1{margin:2px 0 0;font-size:20px;line-height:1.1}.header-left p{margin:4px 0 0;color:var(--muted);font-size:12px}.header-actions{display:flex;gap:10px}.mobile-menu{display:none;border:0;background:transparent;font-size:24px}.course-shell{display:grid;grid-template-columns:248px minmax(0,1fr);min-height:calc(100vh - 84px)}.course-sidebar{position:sticky;top:84px;align-self:start;height:calc(100vh - 84px);padding:24px 16px;background:linear-gradient(180deg,#082f49,#071f32);color:white;overflow:auto}.sidebar-caption{padding:0 12px 12px;color:#8eb6ca;font-size:10px;font-weight:900;letter-spacing:.14em}.course-sidebar nav{display:grid;gap:5px}.course-sidebar nav button{width:100%;display:grid;grid-template-columns:32px 1fr 20px;align-items:center;gap:10px;padding:12px;border:0;border-radius:12px;background:transparent;color:#b9cfdb;text-align:left;cursor:pointer}.course-sidebar nav button span{font-size:10px;font-weight:900;opacity:.65}.course-sidebar nav button b{font-size:13px}.course-sidebar nav button em{font-style:normal;font-size:19px;opacity:.45}.course-sidebar nav button:hover,.course-sidebar nav button.active{background:rgba(255,255,255,.1);color:white}.course-sidebar nav button.active{box-shadow:inset 3px 0 var(--orange)}.sidebar-finance{margin-top:28px;padding:17px;border:1px solid rgba(255,255,255,.12);border-radius:16px;background:rgba(255,255,255,.06)}.sidebar-finance>span{font-size:9px;font-weight:900;letter-spacing:.14em;color:#8eb6ca}.sidebar-finance strong{display:block;margin-top:8px;font-size:21px}.sidebar-finance small,.sidebar-finance p{color:#a8c3d2;font-size:11px}.mini-progress,.balance-bar{height:6px;border-radius:999px;background:#dce8ed;overflow:hidden}.mini-progress{margin:12px 0;background:rgba(255,255,255,.12)}.mini-progress i,.balance-bar i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,var(--orange),#ffb36f)}.course-main{min-width:0;padding:28px;display:grid;align-content:start;gap:22px}.panel{background:white;border:1px solid var(--line);border-radius:18px;box-shadow:var(--shadow)}.button{min-height:42px;padding:0 16px;border:1px solid transparent;border-radius:11px;font-weight:800;font-size:12px;cursor:pointer;transition:.2s}.button:hover{transform:translateY(-1px)}.button:disabled{opacity:.55;cursor:not-allowed;transform:none}.button.primary{background:var(--navy);color:white}.button.ghost{background:white;color:var(--navy);border-color:var(--line)}.button.white{background:white;color:var(--navy)}.button.transparent{border-color:rgba(255,255,255,.35);background:transparent;color:white}.button.compact{min-height:34px;padding:0 11px;font-size:11px}.hero-panel{display:grid;grid-template-columns:minmax(0,1fr) 220px;gap:28px;padding:34px;border-radius:22px;color:white;background:radial-gradient(circle at 82% 22%,rgba(244,123,32,.42),transparent 28%),linear-gradient(135deg,#082f49,#0d6176);box-shadow:0 20px 60px rgba(8,47,73,.18)}.eyebrow.light{color:#c8e5ee}.hero-panel h2{max-width:760px;margin:8px 0 10px;font-size:clamp(24px,3vw,38px);line-height:1.12}.hero-panel p{max-width:720px;margin:0;color:#c7dce6;line-height:1.7}.hero-actions{display:flex;gap:10px;margin-top:23px}.hero-score{align-self:center;display:grid;place-items:center;padding:24px;border-radius:20px;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.09);text-align:center}.hero-score span{font-size:10px;font-weight:900;letter-spacing:.14em}.hero-score strong{font-size:48px}.hero-score small{color:#c7dce6}.metric-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px}.metric-card{position:relative;overflow:hidden;padding:20px;border:1px solid var(--line);border-radius:17px;background:white;box-shadow:var(--shadow)}.metric-card span{font-size:11px;font-weight:800;color:var(--muted)}.metric-card strong{display:block;margin:8px 0 4px;font-size:25px;color:var(--navy)}.metric-card small{font-size:11px;color:var(--muted)}.metric-card>i{position:absolute;right:-18px;bottom:-28px;width:90px;height:90px;border-radius:50%;opacity:.12;background:var(--navy)}.metric-card.orange>i{background:var(--orange)}.metric-card.green>i{background:var(--green)}.metric-card.red>i{background:var(--red)}.metric-card.blue>i{background:var(--blue)}.overview-grid,.report-grid{display:grid;grid-template-columns:1.08fr .92fr;gap:18px}.section-title{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:20px 22px;border-bottom:1px solid var(--line)}.section-title span,.page-title>div>span{font-size:10px;font-weight:900;letter-spacing:.14em;color:var(--orange)}.section-title h3{margin:4px 0 0;font-size:17px}.link-button{border:0;background:transparent;color:var(--blue);font-weight:800;cursor:pointer}.activity-list{padding:7px 14px 14px}.activity-row{width:100%;display:grid;grid-template-columns:42px 1fr auto;align-items:center;gap:12px;padding:12px 8px;border:0;border-bottom:1px solid #edf2f4;background:white;text-align:left;cursor:pointer}.activity-row:hover{background:#f8fbfc}.activity-row span b,.person-cell span b{display:block;font-size:13px}.activity-row span small,.person-cell span small,td small{display:block;margin-top:4px;color:var(--muted);font-size:10px}.avatar{flex:none;width:38px;height:38px;display:grid;place-items:center;border-radius:12px;background:#e2eef3;color:var(--navy);font-size:12px;font-weight:900}.avatar.large{width:58px;height:58px;border-radius:18px;font-size:17px}.status{display:inline-flex;align-items:center;gap:6px;width:max-content;padding:6px 9px;border-radius:999px;font-size:9px;font-weight:900;white-space:nowrap}.status i{width:6px;height:6px;border-radius:50%;background:currentColor}.status.success{background:#e4f5ef;color:#08745b}.status.warning{background:#fff2df;color:#a95a00}.status.danger{background:#fde9e7;color:#b43630}.status.neutral{background:#edf2f4;color:#61747f}.timeline-list{padding:12px 20px 18px}.timeline-list article{display:flex;align-items:center;gap:13px;padding:11px 0;border-bottom:1px solid #edf2f4}.date-box{width:46px;height:49px;display:grid;place-items:center;border-radius:12px;background:#eef5f7;color:var(--navy)}.date-box strong{font-size:18px;line-height:1}.date-box span{font-size:9px;text-transform:uppercase}.timeline-list b{display:block;font-size:13px}.timeline-list small{display:block;margin-top:4px;color:var(--muted);font-size:10px}.table-panel{overflow:hidden}.table-wrap{width:100%;overflow:auto}table{width:100%;border-collapse:collapse;min-width:820px}th{padding:12px 15px;background:#f5f8f9;color:#647782;font-size:9px;letter-spacing:.09em;text-align:left;white-space:nowrap}td{padding:14px 15px;border-top:1px solid #edf2f4;font-size:12px;vertical-align:middle}td b{color:var(--ink)}td.right{text-align:right}.person-cell{display:flex;align-items:center;gap:10px}.page-title{display:flex;align-items:flex-end;justify-content:space-between;gap:20px}.page-title h2{margin:6px 0;font-size:28px}.page-title p{max-width:760px;margin:0;color:var(--muted);font-size:13px;line-height:1.6}.toolbar{display:flex;align-items:center;gap:10px;padding:12px}.searchbox{flex:1;display:flex;align-items:center;gap:9px;min-width:220px;padding:0 13px;border:1px solid var(--line);border-radius:11px;background:#fbfcfd}.searchbox input{width:100%;height:40px;border:0;outline:0;background:transparent}.toolbar select{height:42px;min-width:180px;border:1px solid var(--line);border-radius:11px;background:white;padding:0 12px;color:var(--ink)}.course-card-grid,.student-grid,.instructor-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:17px}.course-card,.student-card,.instructor-card{overflow:hidden;border:1px solid var(--line);border-radius:18px;background:white;box-shadow:var(--shadow)}.course-cover{height:205px;position:relative;display:flex;flex-direction:column;justify-content:space-between;padding:15px;background:linear-gradient(145deg,#0d5269,#082f49);background-size:cover;background-position:center;color:white}.cover-placeholder{position:absolute;inset:0;display:grid;place-items:center;font-size:85px;font-weight:900;opacity:.14}.cover-top,.price-row,.student-head,.session-title-row{display:flex;justify-content:space-between;align-items:center;gap:10px;position:relative}.course-code{padding:6px 9px;border-radius:8px;background:rgba(0,0,0,.26);font-size:9px;font-weight:800}.cover-bottom{position:relative}.cover-bottom span{font-size:10px;font-weight:900;color:#bde5ef}.cover-bottom h3{margin:5px 0 0;font-size:21px}.course-body{padding:17px}.course-body>p{height:44px;margin:0;color:var(--muted);font-size:12px;line-height:1.6;overflow:hidden}.course-meta{display:grid;grid-template-columns:repeat(3,1fr);margin:15px 0;border:1px solid var(--line);border-radius:12px}.course-meta span{padding:10px;text-align:center;color:var(--muted);font-size:9px;border-right:1px solid var(--line)}.course-meta span:last-child{border:0}.course-meta b{display:block;color:var(--navy);font-size:16px}.price-row small{display:block;color:var(--muted);font-size:9px}.price-row b{display:block;margin-top:4px;color:var(--navy);font-size:16px}.location{max-width:140px;color:var(--muted);font-size:10px;text-align:right}.card-actions{display:grid;grid-template-columns:1fr 1fr auto;gap:7px;margin-top:15px}.card-actions.three{grid-template-columns:repeat(3,1fr)}.card-actions button,.row-actions button,.table-action{min-height:34px;padding:0 10px;border:1px solid var(--line);border-radius:9px;background:white;color:var(--navy);font-size:10px;font-weight:900;cursor:pointer}.card-actions button:hover,.row-actions button:hover{background:#f2f7f9}.card-actions button.danger,.row-actions button.danger{color:var(--red)}.card-actions button.success,.row-actions button.success{color:var(--green)}.row-actions{display:flex;gap:6px;flex-wrap:wrap}.student-card{padding:18px}.student-head{justify-content:flex-start}.student-head>div:nth-child(2){flex:1}.student-head h3{margin:0;font-size:16px}.student-head p{margin:4px 0;color:var(--blue);font-size:11px;font-weight:700}.student-head small{color:var(--muted);font-size:9px}.student-finance{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin:17px 0 10px}.student-finance span{padding:10px;border-radius:11px;background:#f5f8f9}.student-finance small{display:block;color:var(--muted);font-size:9px}.student-finance b{display:block;margin-top:5px;font-size:12px}.student-finance.big b{font-size:17px}.green-text{color:var(--green)!important}.orange-text{color:var(--orange)!important}.student-info{display:flex;justify-content:space-between;gap:8px;margin-top:12px;color:var(--muted);font-size:10px}.session-grid{display:grid;gap:11px}.session-card{display:grid;grid-template-columns:65px minmax(0,1fr) auto;align-items:center;gap:16px;padding:17px;border:1px solid var(--line);border-radius:16px;background:white;box-shadow:var(--shadow)}.session-date{height:62px;display:grid;place-items:center;border-radius:15px;background:#e8f2f5;color:var(--navy)}.session-date strong{font-size:24px;line-height:1}.session-date span{font-size:9px;text-transform:uppercase}.session-content h3{margin:0;font-size:16px}.session-content p{margin:5px 0;color:var(--blue);font-size:11px;font-weight:700}.session-meta{display:flex;gap:14px;color:var(--muted);font-size:10px;flex-wrap:wrap}.instructor-grid{grid-template-columns:repeat(4,minmax(0,1fr))}.instructor-card{padding:17px}.instructor-photo{width:68px;height:68px;display:grid;place-items:center;margin-bottom:13px;border-radius:20px;background:linear-gradient(145deg,#dcecf2,#aacbd8);background-size:cover;background-position:center;color:var(--navy);font-size:24px;font-weight:900}.instructor-card h3{margin:9px 0 3px;font-size:16px}.instructor-card p{margin:0;color:var(--blue);font-size:11px}.instructor-card small{display:block;margin-top:5px;color:var(--muted);font-size:10px}.specialty-list{display:flex;flex-wrap:wrap;gap:5px;margin:13px 0}.specialty-list span{padding:5px 7px;border-radius:7px;background:#edf4f6;color:var(--navy);font-size:9px;font-weight:800}.instructor-card .bio{color:var(--muted);line-height:1.6}.report-filter{display:flex;align-items:flex-end;gap:12px;padding:15px}.report-filter .field{min-width:180px}.bar-list{padding:14px 20px}.bar-list article{display:grid;grid-template-columns:180px 1fr 120px;align-items:center;gap:13px;padding:11px 0}.bar-list b{display:block;font-size:11px}.bar-list span{font-size:9px;color:var(--muted)}.bar{height:8px;border-radius:999px;background:#e8eef1;overflow:hidden}.bar i{display:block;height:100%;background:linear-gradient(90deg,var(--navy),var(--blue));border-radius:inherit}.bar-list strong{text-align:right;font-size:11px}.donut-summary{display:grid;grid-template-columns:170px 1fr;align-items:center;gap:20px;padding:25px}.donut{width:150px;height:150px;display:grid;place-content:center;border-radius:50%;background:radial-gradient(circle at center,white 54%,transparent 55%),conic-gradient(var(--orange) 0 28%,var(--blue) 28% 67%,var(--green) 67% 100%);text-align:center}.donut strong{font-size:15px}.donut span{font-size:9px;color:var(--muted)}.legend-list{display:grid;gap:12px}.legend-list>div{display:flex;justify-content:space-between;gap:10px;font-size:11px}.legend-list span{display:flex;align-items:center;gap:7px;color:var(--muted)}.legend-list i{width:8px;height:8px;border-radius:50%;background:var(--orange)}.empty{display:grid;place-items:center;gap:4px;padding:28px;color:var(--muted);text-align:center}.empty span{font-size:24px}.empty p{margin:0;font-size:11px}.full-empty{grid-column:1/-1}.loading-state,.error-state{min-height:55vh;display:grid;place-content:center;justify-items:center;text-align:center}.loading-state p,.error-state p{color:var(--muted)}.spinner{width:34px;height:34px;border:4px solid #d8e5ea;border-top-color:var(--orange);border-radius:50%;animation:spin .85s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}.toast{position:fixed;right:24px;bottom:24px;z-index:1000;max-width:430px;padding:14px 18px;border-radius:12px;background:#102f40;color:white;font-size:12px;font-weight:700;box-shadow:0 15px 50px rgba(0,0,0,.2)}.busy-layer{position:fixed;inset:0;z-index:1100;display:grid;place-content:center;justify-items:center;gap:12px;background:rgba(4,25,38,.36);color:white;backdrop-filter:blur(3px)}.nav-backdrop{display:none}.modal-layer{position:fixed;inset:0;z-index:900;display:grid;place-items:center;padding:22px}.modal-backdrop{position:absolute;inset:0;border:0;background:rgba(5,27,43,.68);backdrop-filter:blur(5px)}.modal{position:relative;width:min(680px,100%);max-height:92vh;display:flex;flex-direction:column;border-radius:20px;background:white;box-shadow:0 30px 100px rgba(0,0,0,.28);overflow:hidden}.modal.wide{width:min(1100px,100%)}.modal>header{display:flex;align-items:center;justify-content:space-between;padding:19px 22px;border-bottom:1px solid var(--line)}.modal>header span{font-size:9px;font-weight:900;letter-spacing:.13em;color:var(--orange)}.modal>header h2{margin:4px 0 0;font-size:20px}.modal>header>button{width:36px;height:36px;border:1px solid var(--line);border-radius:10px;background:white;font-size:22px;cursor:pointer}.modal-body{padding:20px;overflow:auto}.form-stack{display:grid;gap:16px}.form-section{border:1px solid var(--line);border-radius:15px;overflow:hidden}.form-section-title{padding:14px 16px;background:#f6f9fa}.form-section-title h3{margin:0;font-size:14px}.form-section-title p{margin:4px 0 0;color:var(--muted);font-size:10px}.form-section-body{display:grid;gap:13px;padding:16px}.form-grid{display:grid;gap:12px}.form-grid.two{grid-template-columns:repeat(2,minmax(0,1fr))}.form-grid.three{grid-template-columns:repeat(3,minmax(0,1fr))}

.form-grid.four{grid-template-columns:repeat(4,minmax(0,1fr))}

.course-upload-box{
  display:grid;
  gap:12px;
  min-width:0;
  padding:14px;
  border:1px solid var(--line);
  border-radius:14px;
  background:#f7fafb;
  overflow:hidden;
}

.course-upload-box>div{
  max-width:100%;
}

.course-upload-box button{
  max-width:100%;
}

.image-remove-button{
  min-height:38px;
  padding:0 14px;
  border:1px solid #efc3bf;
  border-radius:10px;
  background:#fff4f3;
  color:var(--red);
  font-size:10px;
  font-weight:900;
  cursor:pointer;
}

.image-remove-button:hover{
  background:#fde9e7;
}

.course-gallery-preview{
  display:grid;
  grid-template-columns:repeat(4,minmax(0,1fr));
  gap:9px;
}

.course-gallery-item{
  position:relative;
  min-width:0;
  aspect-ratio:1/1;
  overflow:hidden;
  border:1px solid var(--line);
  border-radius:12px;
  background:#e8eef1;
}

.course-gallery-item img{
  width:100%;
  height:100%;
  display:block;
  object-fit:cover;
}

.course-gallery-item button{
  position:absolute;
  top:6px;
  right:6px;
  width:27px;
  height:27px;
  padding:0;
  border:1px solid rgba(255,255,255,.65);
  border-radius:50%;
  background:rgba(8,47,73,.82);
  color:#fff;
  font-size:18px;
  line-height:1;
  cursor:pointer;
  box-shadow:0 5px 14px rgba(0,0,0,.18);
}

.course-gallery-item button:hover{
  background:var(--red);
}

.field{display:grid;gap:7px}



.field>span{font-size:10px;font-weight:900;color:#536875}.field>span b{color:var(--red);margin-left:4px}.field input,.field select,.field textarea,.attendance-list select,.attendance-list input{width:100%;border:1px solid var(--line);border-radius:10px;background:white;color:var(--ink);outline:0}.field input,.field select{height:42px;padding:0 11px}.field textarea{padding:11px;resize:vertical}.field input:focus,.field select:focus,.field textarea:focus{border-color:#74a8ba;box-shadow:0 0 0 3px rgba(14,116,144,.09)}.switch-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}.switch{display:flex;align-items:center;gap:9px;padding:10px;border:1px solid var(--line);border-radius:11px;background:white;color:var(--muted);font-size:10px;font-weight:800;cursor:pointer}.switch i{width:32px;height:18px;padding:2px;border-radius:999px;background:#cbd7dc}.switch i em{display:block;width:14px;height:14px;border-radius:50%;background:white;transition:.2s}.switch.on{color:var(--navy);border-color:#a4c5d0;background:#f5fafb}.switch.on i{background:var(--green)}.switch.on i em{transform:translateX(14px)}.modal-actions{display:flex;justify-content:flex-end;gap:9px;padding-top:4px}.application-summary,.finance-summary-card{display:flex;align-items:center;gap:13px;padding:14px;border-radius:14px;background:#f3f7f8}.application-summary>div:nth-child(2){flex:1}.application-summary h3{margin:0}.application-summary p,.application-summary small{display:block;margin:3px 0;color:var(--muted);font-size:10px}.detail-note{padding:13px;border-left:3px solid var(--orange);background:#fff9f4}.detail-note b{font-size:11px}.detail-note p{margin:5px 0 0;color:var(--muted);font-size:11px;line-height:1.6}.approval-finance{padding:15px;border:1px solid #b8d9d0;border-radius:14px;background:#f1faf7}.approval-finance h4{margin:0}.approval-finance p{margin:4px 0 13px;color:var(--muted);font-size:10px}.finance-summary-card{justify-content:space-between}.finance-summary-card span{display:block;color:var(--muted);font-size:9px}.finance-summary-card b,.finance-summary-card small{display:block;margin-top:4px}.finance-summary-card strong{display:block;margin-top:5px;color:var(--orange);font-size:22px}.integration-note{display:grid;gap:4px;padding:13px;border-radius:12px;background:#eef6f8}.integration-note b{font-size:11px;color:var(--navy)}.integration-note span{font-size:10px;color:var(--muted)}.student-detail-layout{display:grid;grid-template-columns:250px 1fr;gap:18px}.student-profile{padding:20px;border-radius:16px;background:linear-gradient(145deg,#082f49,#0e667b);color:white}.student-profile h3{margin:14px 0 4px}.student-profile p{margin:0 0 12px;color:#b8dce7;font-size:11px}.profile-info{display:grid;gap:12px;margin-top:20px}.profile-info span{font-size:10px;color:#d1e5eb}.profile-info b{display:block;margin-bottom:3px;color:#84b5c6;font-size:8px;text-transform:uppercase;letter-spacing:.1em}.student-detail-main{min-width:0}.student-detail-main h4{margin:18px 0 8px}.installment-list,.payment-list{display:grid;gap:7px}.installment-list article,.payment-list article{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:11px;border:1px solid var(--line);border-radius:10px}.installment-list span,.payment-list span{font-size:11px}.installment-list small,.payment-list small{display:block;margin-top:3px;color:var(--muted);font-size:9px}.modal-loading{min-height:260px;display:grid;place-content:center;justify-items:center;gap:12px}.attendance-list{display:grid;gap:8px}.attendance-list article{display:grid;grid-template-columns:minmax(220px,1fr) 160px 150px minmax(180px,1fr);align-items:center;gap:10px;padding:10px;border:1px solid var(--line);border-radius:11px}.attendance-list select,.attendance-list input{height:40px;padding:0 10px}


@media(max-width:1180px){.course-card-grid,.student-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.instructor-grid{grid-template-columns:repeat(3,minmax(0,1fr))}.form-grid.four{grid-template-columns:repeat(2,minmax(0,1fr))}.switch-grid{grid-template-columns:repeat(2,1fr)}}
@media(max-width:900px){.course-header{padding:14px 16px}.header-left p{display:none}.header-actions .ghost{display:none}.mobile-menu{display:block}.course-shell{display:block}.course-sidebar{position:fixed;top:0;left:0;z-index:800;width:270px;height:100vh;transform:translateX(-102%);transition:.25s}.course-sidebar.open{transform:translateX(0)}.nav-backdrop{display:block;position:fixed;inset:0;z-index:700;border:0;background:rgba(0,0,0,.4)}.course-main{padding:18px}.hero-panel{grid-template-columns:1fr}.hero-score{display:none}.metric-grid{grid-template-columns:repeat(2,1fr)}.overview-grid,.report-grid{grid-template-columns:1fr}.page-title{align-items:flex-start}.instructor-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.student-detail-layout{grid-template-columns:1fr}.student-profile{display:grid;grid-template-columns:auto 1fr;gap:8px}.profile-info{grid-column:1/-1}.attendance-list article{grid-template-columns:1fr 150px}.attendance-list input{grid-column:1/-1}}
@media(max-width:640px){
  .course-gallery-preview{
    grid-template-columns:repeat(2,minmax(0,1fr));
  }

.course-header h1{font-size:16px}.brand-mark{display:none}.header-actions .button{min-height:38px;padding:0 11px}.course-main{padding:13px;gap:15px}.hero-panel{padding:23px}.hero-panel h2{font-size:25px}.hero-actions{flex-direction:column}.metric-grid,.course-card-grid,.student-grid,.instructor-grid{grid-template-columns:1fr}.metric-card strong{font-size:21px}.page-title{display:grid}.page-title h2{font-size:24px}.toolbar{display:grid}.toolbar select{width:100%}.session-card{grid-template-columns:52px 1fr}.session-card>.button{grid-column:1/-1}.form-grid.two,.form-grid.three,.form-grid.four,.switch-grid{grid-template-columns:1fr}.modal-layer{padding:8px}.modal{max-height:97vh;border-radius:16px}.modal-body{padding:14px}.report-filter{display:grid}.report-filter .field{min-width:0}.donut-summary{grid-template-columns:1fr;justify-items:center}.bar-list article{grid-template-columns:1fr}.bar-list strong{text-align:left}.attendance-list article{grid-template-columns:1fr}.attendance-list input{grid-column:auto}.student-finance{grid-template-columns:1fr}.course-cover{height:190px}}
@media print{.course-header,.course-sidebar,.header-actions,.button,.toolbar,.toast{display:none!important}.course-shell{display:block}.course-main{padding:0}.panel,.metric-card{box-shadow:none}.course-app{background:white}}


/* =========================================================
   HOTEL PANEL TASARIM DILI - KURS / EGITIM / SPOR
   Mevcut form, tablo ve islem siniflari korunur; yalniz kabuk ve
   dashboard otel paneli ile ayni kurumsal akisa getirilir.
   ========================================================= */
.course-app{
  --hotel-blue:#153c70;
  --hotel-blue-2:#274f86;
  --hotel-orange:#ef7d2c;
  --hotel-bg:#f3f6fa;
  --hotel-border:#dfe6ef;
  background:
    radial-gradient(circle at 94% 4%,rgba(239,125,44,.11),transparent 25%),
    linear-gradient(180deg,#eef2f7 0,#f7f9fc 165px,#f3f6fa 100%);
  padding:18px;
}
.hotel-course-workspace{width:100%;max-width:none;margin:0 auto;display:grid;gap:12px}
.hotel-course-header{
  position:relative;z-index:5;display:flex;align-items:center;justify-content:space-between;gap:24px;
  min-height:88px;padding:17px 22px;border-radius:17px;color:#fff;overflow:hidden;
  background:
    radial-gradient(circle at 46% 110%,rgba(255,255,255,.08) 0 42px,transparent 43px),
    radial-gradient(circle at 94% 0,rgba(255,255,255,.08) 0 80px,transparent 81px),
    linear-gradient(105deg,#15365f 0%,#31598d 55%,#ed7d2d 120%);
  box-shadow:0 13px 35px rgba(26,52,88,.18)
}
.hotel-course-header:after{content:"";position:absolute;inset:auto -30px -85px auto;width:190px;height:190px;border-radius:50%;background:rgba(255,255,255,.05)}
.hotel-course-brand{position:relative;z-index:2;display:flex;align-items:center;gap:15px;min-width:0}
.hotel-course-logo{width:48px;height:48px;display:grid;place-items:center;flex:0 0 48px;border-radius:13px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.2);font-size:25px;box-shadow:inset 0 1px rgba(255,255,255,.2)}
.hotel-course-brand h1{margin:0;font-size:25px;line-height:1.1;letter-spacing:.01em;color:#fff}
.hotel-course-brand p{margin:5px 0 0;max-width:720px;color:rgba(255,255,255,.82);font-size:11px}
.hotel-course-actions{position:relative;z-index:2;display:flex;align-items:center;justify-content:flex-end;gap:7px;flex-wrap:wrap}
.hotel-live-chip,.hotel-rate-chip{display:inline-flex;align-items:center;gap:6px;min-height:31px;padding:0 10px;border-radius:999px;background:rgba(255,255,255,.14);border:1px solid rgba(255,255,255,.16);color:#fff;font-size:9px;font-weight:900;letter-spacing:.04em}
.hotel-live-chip i{width:7px;height:7px;border-radius:50%;background:#61d892;box-shadow:0 0 0 4px rgba(97,216,146,.15)}
.hotel-header-button{min-height:34px;padding:0 12px;border-radius:999px;border:1px solid rgba(255,255,255,.18);font-size:9px;font-weight:900;cursor:pointer;transition:.2s}
.hotel-header-button:hover{transform:translateY(-1px)}
.hotel-header-button.secondary{background:rgba(255,255,255,.13);color:#fff}
.hotel-header-button.primary{background:#fff;color:#173b6c;box-shadow:0 7px 20px rgba(14,35,64,.14)}
.hotel-course-tabs{display:grid;grid-template-columns:repeat(9,minmax(105px,1fr));gap:6px;padding:6px;border:1px solid var(--hotel-border);border-radius:15px;background:rgba(255,255,255,.88);box-shadow:0 7px 23px rgba(30,55,88,.09);overflow-x:auto}
.hotel-course-tabs button{position:relative;min-width:108px;min-height:49px;display:flex;align-items:center;gap:8px;padding:8px 10px;border:0;border-radius:10px;background:#fff;color:#172b47;font-size:10px;font-weight:800;text-align:left;cursor:pointer;box-shadow:inset 0 0 0 1px #e8edf4;transition:.2s}
.hotel-course-tabs button:hover{background:#f6f8fb}
.hotel-course-tabs button.active{color:#fff;background:linear-gradient(135deg,#1b4579,#294f82);box-shadow:0 7px 18px rgba(28,66,112,.22)}
.hotel-course-tabs button.active:after{content:"";position:absolute;left:10px;right:10px;bottom:0;height:3px;border-radius:3px 3px 0 0;background:var(--hotel-orange)}
.hotel-tab-icon{width:27px;height:27px;display:grid;place-items:center;flex:0 0 27px;border-radius:8px;background:#eff4fb;font-size:13px}
.hotel-course-tabs button.active .hotel-tab-icon{background:rgba(255,255,255,.15)}
.course-shell{display:block!important;min-height:auto!important}.course-sidebar{display:none!important}.course-main{padding:5px 0 18px;gap:12px}.nav-backdrop{display:none!important}
.hotel-quick-strip{display:grid;grid-template-columns:1fr 1.9fr .9fr;gap:10px;padding:1px 0}
.hotel-quick-strip label{display:grid;gap:5px}.hotel-quick-strip label>span{font-size:9px;color:#7b8898}.hotel-quick-strip select,.hotel-quick-strip input{height:40px;padding:0 12px;border:0;border-bottom:1px solid #dfe3e9;background:rgba(255,255,255,.38);color:#26364b;outline:0;font-size:10px}
.hotel-quick-strip select:focus,.hotel-quick-strip input:focus{border-bottom-color:var(--hotel-orange)}
.hotel-metric-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}
.metric-card{position:relative;min-height:91px;padding:15px;border-radius:13px;border:1px solid #e0e7f0;background:linear-gradient(145deg,#fff 0%,#fff 75%,rgba(238,244,251,.72) 100%);box-shadow:0 7px 22px rgba(30,52,84,.06);overflow:hidden}
.metric-card:before{content:"";position:absolute;left:0;right:0;top:0;height:3px;background:#1d4e87}.metric-card.orange:before{background:#f17b26}.metric-card.green:before{background:#58a455}.metric-card.red:before{background:#ef7d2c}.metric-card.purple:before{background:#9a58b5}.metric-card.blue:before{background:#6c8cae}
.metric-card>span{display:block;color:#607087;font-size:9px;font-weight:800}.metric-card>strong{display:block;margin-top:11px;color:#0f2038;font-size:21px;line-height:1}.metric-card>small{display:block;margin-top:7px;color:#8290a1;font-size:8px}.metric-card>i{position:absolute;right:-13px;bottom:-16px;width:62px;height:62px;border-radius:50%;background:rgba(31,76,129,.04)}
.metric-icon{position:absolute;right:12px;top:12px;width:29px;height:29px;display:grid;place-items:center;border-radius:9px;background:#edf3fb;font-style:normal;font-size:14px;box-shadow:0 4px 12px rgba(33,66,109,.08)}
.hotel-overview-layout{display:grid;grid-template-columns:minmax(0,2.05fr) minmax(310px,1fr);gap:10px;align-items:stretch}
.hotel-recent-panel{min-height:390px;border-radius:13px;box-shadow:0 8px 24px rgba(30,52,84,.07)}
.hotel-activity-list{min-height:310px}.hotel-activity-list .activity-row{padding:13px 18px}
.hotel-summary-panel{min-height:390px;padding:14px 0 0;border-radius:13px;color:#fff;overflow:hidden;background:linear-gradient(145deg,#173c70,#2c5489);box-shadow:0 13px 32px rgba(25,58,104,.2)}
.hotel-summary-title{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:0 14px}.hotel-summary-title span{font-size:8px;font-weight:900;letter-spacing:.12em;color:rgba(255,255,255,.72)}.hotel-summary-title b{padding:4px 7px;border-radius:999px;background:rgba(255,255,255,.14);font-size:8px}.hotel-summary-panel h3{margin:5px 14px 8px;font-size:15px}
.hotel-summary-ring{--ring-value:0deg;width:112px;height:112px;display:grid;place-items:center;margin:12px auto;border-radius:50%;background:conic-gradient(#7795bf var(--ring-value),rgba(255,255,255,.10) 0);position:relative}.hotel-summary-ring:before{content:"";position:absolute;inset:9px;border-radius:50%;background:#244c80;box-shadow:inset 0 0 0 1px rgba(255,255,255,.08)}.hotel-summary-ring>div{position:relative;text-align:center}.hotel-summary-ring span{display:block;font-size:7px;font-weight:900;letter-spacing:.13em;color:#c5d5e9}.hotel-summary-ring strong{display:block;margin-top:3px;font-size:25px}
.hotel-summary-income{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 12px;background:rgba(255,255,255,.10);border-top:1px solid rgba(255,255,255,.08);border-bottom:1px solid rgba(255,255,255,.08)}.hotel-summary-income span{font-size:8px;color:#d1dff0}.hotel-summary-income strong{font-size:13px}
.hotel-summary-rows{display:grid}.hotel-summary-rows>div{display:flex;align-items:center;justify-content:space-between;gap:12px;min-height:32px;padding:0 12px;border-bottom:1px solid rgba(255,255,255,.10);font-size:8px}.hotel-summary-rows span{color:#d5e0ef}.hotel-summary-rows b{font-size:10px}
.hotel-lower-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}.hotel-lower-grid>.panel{border-radius:13px;min-height:250px}.section-title{padding:13px 15px}.section-title span{font-size:8px}.section-title h3{font-size:15px}
.schedule-list article,.due-list article{padding:10px 14px}.schedule-list .date-box{width:42px;height:42px;border-radius:10px}.panel,.course-card,.student-card,.session-card{border-radius:13px}.page-title h2{font-size:22px}.page-title{margin-top:4px}.toolbar{border-radius:12px}
@media(max-width:1350px){.hotel-course-tabs{grid-template-columns:repeat(9,minmax(120px,1fr))}.hotel-metric-grid{grid-template-columns:repeat(4,minmax(0,1fr))}}
@media(max-width:1050px){.hotel-course-header{align-items:flex-start}.hotel-course-actions{max-width:420px}.hotel-metric-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.hotel-overview-layout{grid-template-columns:1fr}.hotel-summary-panel{min-height:auto}.hotel-lower-grid{grid-template-columns:1fr}.hotel-quick-strip{grid-template-columns:1fr 1fr}.hotel-quick-strip label:last-child{grid-column:1/-1}}
@media(max-width:720px){.course-app{padding:8px}.hotel-course-header{display:grid;padding:15px}.hotel-course-brand h1{font-size:20px}.hotel-course-brand p{font-size:10px}.hotel-course-actions{justify-content:flex-start}.hotel-live-chip,.hotel-rate-chip{display:none}.hotel-course-tabs{display:flex}.hotel-course-tabs button{flex:0 0 135px}.hotel-quick-strip{grid-template-columns:1fr}.hotel-quick-strip label:last-child{grid-column:auto}.hotel-metric-grid{grid-template-columns:1fr}.course-main{padding-top:2px}.hotel-course-brand{align-items:flex-start}.hotel-course-logo{width:42px;height:42px;flex-basis:42px}.hotel-header-button{flex:1}.hotel-overview-layout{display:block}.hotel-summary-panel{margin-top:10px}}
@media print{.hotel-course-header,.hotel-course-tabs,.hotel-quick-strip,.hotel-course-actions{display:none!important}.course-app{padding:0;background:#fff}.course-main{padding:0}}

`;
